// Factory.js — Gestión de producción (insumos, recetas, costos, órdenes). Versión optimizada.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis } from 'recharts';
import './Factory.css';
import Spinner from './Spinner';
import Notification from './Notification';
import { FaCheckCircle, FaTrash, FaArchive, FaUndo, FaTimesCircle, FaPlus } from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { readStoreThemeIsDark, STORE_THEME_CHANGE_EVENT } from '../utils/storeTheme';

const RAW_MATERIALS_COLLECTION = 'factory_raw_materials';
const RECIPES_COLLECTION = 'factory_recipes';
const LABOR_COSTS_COLLECTION = 'factory_labor_costs';
const PRODUCTION_ORDERS_COLLECTION = 'factory_production_orders';
const MATERIAL_CATEGORIES_COLLECTION = 'factory_material_categories';

/** Firestore Timestamp | Date | número | ISO -> Date o null */
const coerceToDate = (value) => {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1e6);
  }
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const parseLocalDateStart = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const p = iso.split('-').map(Number);
  if (p.length !== 3 || p.some(Number.isNaN)) return null;
  return new Date(p[0], p[1] - 1, p[2], 0, 0, 0, 0);
};

const parseLocalDateEnd = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const p = iso.split('-').map(Number);
  if (p.length !== 3 || p.some(Number.isNaN)) return null;
  return new Date(p[0], p[1] - 1, p[2], 23, 59, 59, 999);
};

/**
 * Módulo genérico de gestión de producción (cualquier producto fabricado).
 * VERSIÓN OPTIMIZADA:
 * - Actualizaciones locales sin recargas completas
 * - Reseteo automático de formularios
 * - Loading granular por operación
 * - Mejor manejo de errores
 * - Performance optimizada
 */
const Factory = () => {
  // Estados principales
  const [notification, setNotification] = useState({ message: '', show: false });
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Estados de datos
  const [rawMaterials, setRawMaterials] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [laborCosts, setLaborCosts] = useState([]);
  const [productionOrders, setProductionOrders] = useState([]);
  const [materialCategories, setMaterialCategories] = useState([]);
  
  // Estados de loading granular
  const [loadingStates, setLoadingStates] = useState({
    initial: false,
    addingMaterial: false,
    updatingMaterial: false,
    deletingMaterial: false,
    addingRecipe: false,
    updatingRecipe: false,
    deletingRecipe: false,
    addingLabor: false,
    updatingLabor: false,
    deletingLabor: false,
    creatingOrder: false,
    updatingOrder: false,
    deletingOrder: false
  });

  // Estados para formularios de insumos
  const [newMaterial, setNewMaterial] = useState({ name: '', unit: '', cost: '', stock: '', categoryId: '' });
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingMaterial, setEditingMaterial] = useState({ name: '', unit: '', cost: '', stock: '', categoryId: '' });

  // Estados para formularios de recetas
  const [newRecipe, setNewRecipe] = useState({ name: '', ingredients: [], laborItems: [], margin: 30 });
  const [editingRecipeIndex, setEditingRecipeIndex] = useState(null);
  const [editingRecipe, setEditingRecipe] = useState({ name: '', ingredients: [], laborItems: [], margin: 30 });
  const [ingredientToAdd, setIngredientToAdd] = useState({ material: '', quantity: '' });
  const [ingredientToAddEdit, setIngredientToAddEdit] = useState({ material: '', quantity: '' });

  // Estados para formularios de mano de obra
  const [newLabor, setNewLabor] = useState({ name: '', cost: '', description: '' });
  const [editingLaborIndex, setEditingLaborIndex] = useState(null);
  const [editingLabor, setEditingLabor] = useState({ name: '', cost: '', description: '' });

  // Estados para recetas - mano de obra
  const [laborToAdd, setLaborToAdd] = useState('');
  const [laborToAddEdit, setLaborToAddEdit] = useState('');

  // Estado para órdenes de producción
  const [showArchivedOrders, setShowArchivedOrders] = useState(false);
  const [productionHistoryFrom, setProductionHistoryFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [productionHistoryTo, setProductionHistoryTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [newOrder, setNewOrder] = useState({ operator: '', recipeId: '', quantity: 1 });

  // NUEVO: Estados para categorías
  const [newCategory, setNewCategory] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');

  // NUEVO: Estados para cambio de modo gráfico
  const [materialsChartMode, setMaterialsChartMode] = useState('insumos'); // 'insumos' o 'categorias'

  // NUEVO: Estados para mostrar/ocultar el formulario de agregar insumo
  const [showAddMaterialForm, setShowAddMaterialForm] = useState(false);

  // NUEVO: Estados para determinar si es desktop
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  const [isDarkTheme, setIsDarkTheme] = useState(() => readStoreThemeIsDark());

  // ==================== UTILIDADES Y HELPERS ====================

  /**
   * Actualiza un estado de loading específico
   */
  const setLoading = useCallback((operation, value) => {
    setLoadingStates(prev => ({ ...prev, [operation]: value }));
  }, []);

  /**
   * Resetea un formulario específico
   */
  const resetForm = useCallback((formType) => {
    switch (formType) {
      case 'material':
        setNewMaterial({ name: '', unit: '', cost: '', stock: '', categoryId: '' });
        break;
      case 'recipe':
        setNewRecipe({ name: '', ingredients: [], laborItems: [], margin: 30 });
        setIngredientToAdd({ material: '', quantity: '' });
        setLaborToAdd('');
        break;
      case 'labor':
        setNewLabor({ name: '', cost: '', description: '' });
        break;
      case 'order':
        setNewOrder({ operator: '', recipeId: '', quantity: 1 });
        break;
      case 'editing':
        setEditingIndex(null);
        setEditingRecipeIndex(null);
        setEditingLaborIndex(null);
        setIngredientToAddEdit({ material: '', quantity: '' });
        setLaborToAddEdit('');
        break;
      default:
        break;
    }
  }, []);

  /**
   * Muestra una notificación al usuario
   */
  const showMessage = useCallback((message) => {
    setNotification({ message, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  }, []);

  /**
   * Carga los datos iniciales desde Firestore
   */
  useEffect(() => {
    const fetchData = async () => {
      setLoading('initial', true);
      try {
        const [materialsSnap, recipesSnap, laborSnap, ordersSnap] = await Promise.all([
          getDocs(collection(db, RAW_MATERIALS_COLLECTION)),
          getDocs(collection(db, RECIPES_COLLECTION)),
          getDocs(collection(db, LABOR_COSTS_COLLECTION)),
          getDocs(collection(db, PRODUCTION_ORDERS_COLLECTION))
        ]);

        const materialsData = materialsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const recipesData = recipesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const laborData = laborSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const ordersData = ordersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        setRawMaterials(materialsData);
        setRecipes(recipesData);
        setLaborCosts(laborData);
        setProductionOrders(ordersData);
        
        showMessage(`Sistema cargado: ${materialsData.length} insumos, ${recipesData.length} recetas, ${laborData.length} manos de obra, ${ordersData.length} órdenes`);
      } catch (error) {
        console.error('Error cargando datos:', error);
        showMessage('❌ Error al cargar datos de Firebase');
      }
      setLoading('initial', false);
    };
    
    fetchData();
  }, [showMessage, setLoading]);

  // NUEVO: Cargar categorías al inicio
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const snap = await getDocs(collection(db, MATERIAL_CATEGORIES_COLLECTION));
        setMaterialCategories(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (e) {
        setMaterialCategories([]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const sync = () => setIsDarkTheme(readStoreThemeIsDark());
    window.addEventListener(STORE_THEME_CHANGE_EVENT, sync);
    return () => window.removeEventListener(STORE_THEME_CHANGE_EVENT, sync);
  }, []);

  /** Fondo de página propio; sin capa de estrellas (AnimatedBackground no se monta en /factory). */
  useEffect(() => {
    document.body.classList.add('factory-page-bg');
    return () => document.body.classList.remove('factory-page-bg');
  }, []);

  const chartTheme = useMemo(
    () => ({
      tickFill: isDarkTheme ? '#f4f5f7' : '#1a1d24',
      axisStroke: isDarkTheme ? '#5c6370' : '#9aa3b2',
      cardBg: isDarkTheme ? '#23242b' : '#ffffff',
      cardBorder: isDarkTheme ? '#353545' : '#d0d5e0',
      cardShadow: isDarkTheme ? '0 2px 8px rgba(0,0,0,0.18)' : '0 2px 12px rgba(15, 23, 42, 0.08)',
      legendColor: isDarkTheme ? '#f4f5f7' : '#1a1d24',
      hintMuted: isDarkTheme ? '#8b92a0' : '#6b7280',
    }),
    [isDarkTheme]
  );

  // ==================== GESTIÓN DE INSUMOS ====================

  const handleMaterialChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewMaterial(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleAddMaterial = useCallback(async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!newMaterial.name.trim() || !newMaterial.unit.trim() || !newMaterial.cost) {
      showMessage('⚠️ Completa todos los campos obligatorios');
      return;
    }

    if (parseFloat(newMaterial.cost) <= 0) {
      showMessage('⚠️ El precio debe ser mayor a cero');
      return;
    }

    if (rawMaterials.some(mat => 
      mat.name.toLowerCase() === newMaterial.name.toLowerCase().trim()
    )) {
      showMessage('⚠️ Ya existe un insumo con ese nombre');
      return;
    }

    setLoading('addingMaterial', true);
    try {
      const materialData = {
        name: newMaterial.name.trim(),
        unit: newMaterial.unit.trim(),
        cost: parseFloat(newMaterial.cost),
        stock: parseFloat(newMaterial.stock) || 0,
        categoryId: newMaterial.categoryId,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, RAW_MATERIALS_COLLECTION), materialData);
      
      // Actualización local optimista
      setRawMaterials(prev => [...prev, { ...materialData, id: docRef.id }]);
      resetForm('material');
      showMessage('Insumo agregado correctamente');
    } catch (error) {
      console.error('Error agregando insumo:', error);
      showMessage('❌ Error al agregar insumo');
    }
    setLoading('addingMaterial', false);
  }, [newMaterial, rawMaterials, showMessage, setLoading, resetForm]);

  const handleEditMaterial = useCallback((index) => {
    setEditingIndex(index);
    setEditingMaterial({ ...rawMaterials[index] });
  }, [rawMaterials]);

  const handleEditingMaterialChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditingMaterial(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSaveEditMaterial = useCallback(async (index) => {
    // Validaciones
    if (!editingMaterial.name.trim() || !editingMaterial.unit.trim() || !editingMaterial.cost) {
      showMessage('⚠️ Completa todos los campos obligatorios');
      return;
    }

    if (parseFloat(editingMaterial.cost) <= 0) {
      showMessage('⚠️ El precio debe ser mayor a cero');
      return;
    }

    if (rawMaterials.some((mat, i) => 
      i !== index && mat.name.toLowerCase() === editingMaterial.name.toLowerCase().trim()
    )) {
      showMessage('⚠️ Ya existe un insumo con ese nombre');
      return;
    }

    setLoading('updatingMaterial', true);
    try {
      const materialData = {
        name: editingMaterial.name.trim(),
        unit: editingMaterial.unit.trim(),
        cost: parseFloat(editingMaterial.cost),
        stock: parseFloat(editingMaterial.stock) || 0,
        categoryId: editingMaterial.categoryId,
        updatedAt: new Date()
      };

      await updateDoc(doc(db, RAW_MATERIALS_COLLECTION, editingMaterial.id), materialData);
      
      // Actualización local optimista
      setRawMaterials(prev => prev.map((mat, i) => 
        i === index ? { ...editingMaterial, ...materialData } : mat
      ));
      resetForm('editing');
      showMessage('Insumo actualizado - Costos recalculados automáticamente');
    } catch (error) {
      console.error('Error actualizando insumo:', error);
      showMessage('❌ Error al actualizar insumo');
    }
    setLoading('updatingMaterial', false);
  }, [editingMaterial, rawMaterials, showMessage, setLoading, resetForm]);

  const handleDeleteMaterial = useCallback(async (index) => {
    const material = rawMaterials[index];
    
    const usedInRecipes = recipes.filter(recipe => 
      recipe.ingredients.some(ing => ing.material === material.name)
    );

    if (usedInRecipes.length > 0) {
      const recipeNames = usedInRecipes.map(r => r.name).join(', ');
      if (!window.confirm(
        `⚠️ Este insumo está siendo usado en las recetas: ${recipeNames}\n\n¿Estás seguro de eliminarlo? Esto afectará los cálculos de costo.`
      )) return;
    } else {
      if (!window.confirm('¿Eliminar este insumo?')) return;
    }

    setLoading('deletingMaterial', true);
    try {
      await deleteDoc(doc(db, RAW_MATERIALS_COLLECTION, material.id));
      
      // Actualización local optimista
      setRawMaterials(prev => prev.filter((_, i) => i !== index));
      showMessage('Insumo eliminado');
    } catch (error) {
      console.error('Error eliminando insumo:', error);
      showMessage('❌ Error al eliminar insumo');
    }
    setLoading('deletingMaterial', false);
  }, [rawMaterials, recipes, showMessage, setLoading]);

  // ==================== GESTIÓN DE MANO DE OBRA ====================

  const handleLaborChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewLabor(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleAddLabor = useCallback(async (e) => {
    e.preventDefault();
    if (!newLabor.name.trim() || !newLabor.cost) {
      showMessage('⚠️ Completa todos los campos obligatorios');
      return;
    }

    if (parseFloat(newLabor.cost) <= 0) {
      showMessage('⚠️ El costo debe ser mayor a cero');
      return;
    }

    setLoading('addingLabor', true);
    try {
      const laborData = {
        name: newLabor.name.trim(),
        cost: parseFloat(newLabor.cost),
        description: newLabor.description.trim() || '',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, LABOR_COSTS_COLLECTION), laborData);
      
      // Actualización local optimista
      setLaborCosts(prev => [...prev, { ...laborData, id: docRef.id }]);
      resetForm('labor');
      showMessage('Mano de obra agregada');
    } catch (error) {
      console.error('Error agregando mano de obra:', error);
      showMessage('❌ Error al agregar mano de obra');
    }
    setLoading('addingLabor', false);
  }, [newLabor, showMessage, setLoading, resetForm]);

  const handleEditLabor = useCallback((index) => {
    setEditingLaborIndex(index);
    setEditingLabor({ ...laborCosts[index] });
  }, [laborCosts]);

  const handleEditingLaborChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditingLabor(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSaveEditLabor = useCallback(async (index) => {
    if (!editingLabor.name.trim() || !editingLabor.cost) {
      showMessage('⚠️ Completa todos los campos obligatorios');
      return;
    }

    if (parseFloat(editingLabor.cost) <= 0) {
      showMessage('⚠️ El costo debe ser mayor a cero');
      return;
    }

    setLoading('updatingLabor', true);
    try {
      const laborData = {
        name: editingLabor.name.trim(),
        cost: parseFloat(editingLabor.cost),
        description: editingLabor.description.trim() || '',
        updatedAt: new Date()
      };

      await updateDoc(doc(db, LABOR_COSTS_COLLECTION, editingLabor.id), laborData);
      
      // Actualización local optimista
      setLaborCosts(prev => prev.map((labor, i) => 
        i === index ? { ...editingLabor, ...laborData } : labor
      ));
      resetForm('editing');
      showMessage('Mano de obra actualizada');
    } catch (error) {
      console.error('Error actualizando mano de obra:', error);
      showMessage('❌ Error al actualizar mano de obra');
    }
    setLoading('updatingLabor', false);
  }, [editingLabor, showMessage, setLoading, resetForm]);

  const handleDeleteLabor = useCallback(async (index) => {
    if (!window.confirm('¿Eliminar esta mano de obra?')) return;
    
    setLoading('deletingLabor', true);
    try {
      await deleteDoc(doc(db, LABOR_COSTS_COLLECTION, laborCosts[index].id));
      
      // Actualización local optimista
      setLaborCosts(prev => prev.filter((_, i) => i !== index));
      showMessage('Mano de obra eliminada');
    } catch (error) {
      console.error('Error eliminando mano de obra:', error);
      showMessage('❌ Error al eliminar mano de obra');
    }
    setLoading('deletingLabor', false);
  }, [laborCosts, showMessage, setLoading]);

  // ==================== GESTIÓN DE RECETAS ====================

  const handleRecipeChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewRecipe(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleAddIngredientToRecipe = useCallback((e) => {
    e.preventDefault();
    
    if (!ingredientToAdd.material || !ingredientToAdd.quantity) {
      showMessage('⚠️ Selecciona un insumo y especifica la cantidad');
      return;
    }

    if (parseFloat(ingredientToAdd.quantity) <= 0) {
      showMessage('⚠️ La cantidad debe ser mayor a cero');
      return;
    }

    if (newRecipe.ingredients.some(ing => ing.material === ingredientToAdd.material)) {
      showMessage('⚠️ Ese insumo ya fue agregado a la receta');
      return;
    }

    setNewRecipe(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { 
        material: ingredientToAdd.material, 
        quantity: parseFloat(ingredientToAdd.quantity) 
      }]
    }));
    setIngredientToAdd({ material: '', quantity: '' });
    showMessage('Insumo agregado a la receta');
  }, [ingredientToAdd, newRecipe.ingredients, showMessage]);

  const handleRemoveIngredientFromRecipe = useCallback((index) => {
    setNewRecipe(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  }, []);

  const handleAddLaborToRecipe = useCallback((e) => {
    e.preventDefault();
    
    if (!laborToAdd) {
      showMessage('⚠️ Selecciona una mano de obra');
      return;
    }

    if (newRecipe.laborItems.includes(laborToAdd)) {
      showMessage('⚠️ Esa mano de obra ya fue agregada a la receta');
      return;
    }

    setNewRecipe(prev => ({
      ...prev,
      laborItems: [...prev.laborItems, laborToAdd]
    }));
    setLaborToAdd('');
    showMessage('Mano de obra agregada a la receta');
  }, [laborToAdd, newRecipe.laborItems, showMessage]);

  const handleRemoveLaborFromRecipe = useCallback((index) => {
    setNewRecipe(prev => ({
      ...prev,
      laborItems: prev.laborItems.filter((_, i) => i !== index)
    }));
  }, []);

  const handleAddRecipe = useCallback(async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!newRecipe.name.trim()) {
      showMessage('⚠️ Ingresa el nombre del producto');
      return;
    }

    if (newRecipe.ingredients.length === 0) {
      showMessage('⚠️ Agrega al menos un insumo a la receta');
      return;
    }

    if (!newRecipe.margin || isNaN(newRecipe.margin) || newRecipe.margin < 0) {
      showMessage('⚠️ Ingresa un margen válido');
      return;
    }

    const invalidIngredients = newRecipe.ingredients.filter(ing => 
      !ing.material || !ing.quantity || ing.quantity <= 0
    );
    if (invalidIngredients.length > 0) {
      showMessage('⚠️ Todos los insumos deben tener material y cantidad válida');
      return;
    }

    if (recipes.some(recipe => 
      recipe.name.toLowerCase() === newRecipe.name.toLowerCase().trim()
    )) {
      showMessage('⚠️ Ya existe una receta con ese nombre');
      return;
    }

    setLoading('addingRecipe', true);
    try {
      const recipeData = {
        name: newRecipe.name.trim(),
        ingredients: newRecipe.ingredients,
        laborItems: newRecipe.laborItems,
        margin: parseFloat(newRecipe.margin),
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, RECIPES_COLLECTION), recipeData);
      
      // Actualización local optimista
      setRecipes(prev => [...prev, { ...recipeData, id: docRef.id }]);
      resetForm('recipe');
      showMessage('Receta creada correctamente');
    } catch (error) {
      console.error('Error agregando receta:', error);
      showMessage('❌ Error al agregar receta');
    }
    setLoading('addingRecipe', false);
  }, [newRecipe, recipes, showMessage, setLoading, resetForm]);

  const handleEditRecipe = useCallback((index) => {
    setEditingRecipeIndex(index);
    setEditingRecipe({ 
      ...recipes[index], 
      ingredients: [...recipes[index].ingredients],
      laborItems: [...(recipes[index].laborItems || [])]
    });
  }, [recipes]);

  const handleEditingRecipeChange = useCallback((e) => {
    const { name, value } = e.target;
    setEditingRecipe(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleEditIngredientChange = useCallback((i, field, value) => {
    setEditingRecipe(prev => {
      const updated = [...prev.ingredients];
      updated[i] = { 
        ...updated[i], 
        [field]: field === 'quantity' ? parseFloat(value) || 0 : value 
      };
      return { ...prev, ingredients: updated };
    });
  }, []);

  const handleRemoveIngredientFromEditing = useCallback((i) => {
    setEditingRecipe(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, idx) => idx !== i)
    }));
  }, []);

  const handleAddIngredientToEditing = useCallback((e) => {
    e.preventDefault();
    
    if (!ingredientToAddEdit.material || !ingredientToAddEdit.quantity) {
      showMessage('⚠️ Selecciona un insumo y especifica la cantidad');
      return;
    }

    if (parseFloat(ingredientToAddEdit.quantity) <= 0) {
      showMessage('⚠️ La cantidad debe ser mayor a cero');
      return;
    }

    if (editingRecipe.ingredients.some(ing => ing.material === ingredientToAddEdit.material)) {
      showMessage('⚠️ Ese insumo ya existe en la receta');
      return;
    }

    setEditingRecipe(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { 
        material: ingredientToAddEdit.material, 
        quantity: parseFloat(ingredientToAddEdit.quantity) 
      }]
    }));
    setIngredientToAddEdit({ material: '', quantity: '' });
  }, [ingredientToAddEdit, editingRecipe.ingredients, showMessage]);

  const handleAddLaborToEditing = useCallback((e) => {
    e.preventDefault();
    
    if (!laborToAddEdit) {
      showMessage('⚠️ Selecciona una mano de obra');
      return;
    }

    if (editingRecipe.laborItems.includes(laborToAddEdit)) {
      showMessage('⚠️ Esa mano de obra ya existe en la receta');
      return;
    }

    setEditingRecipe(prev => ({
      ...prev,
      laborItems: [...prev.laborItems, laborToAddEdit]
    }));
    setLaborToAddEdit('');
  }, [laborToAddEdit, editingRecipe.laborItems, showMessage]);

  const handleRemoveLaborFromEditing = useCallback((index) => {
    setEditingRecipe(prev => ({
      ...prev,
      laborItems: prev.laborItems.filter((_, i) => i !== index)
    }));
  }, []);

  const handleSaveEditRecipe = useCallback(async (index) => {
    // Validaciones
    if (!editingRecipe.name.trim()) {
      showMessage('⚠️ Ingresa el nombre del producto');
      return;
    }

    if (editingRecipe.ingredients.length === 0) {
      showMessage('⚠️ La receta debe tener al menos un insumo');
      return;
    }

    if (!editingRecipe.margin || isNaN(editingRecipe.margin) || editingRecipe.margin < 0) {
      showMessage('⚠️ Ingresa un margen válido');
      return;
    }

    const invalidIngredients = editingRecipe.ingredients.filter(ing => 
      !ing.material || !ing.quantity || ing.quantity <= 0
    );
    if (invalidIngredients.length > 0) {
      showMessage('⚠️ Todos los insumos deben tener material y cantidad válida');
      return;
    }

    if (recipes.some((recipe, i) => 
      i !== index && recipe.name.toLowerCase() === editingRecipe.name.toLowerCase().trim()
    )) {
      showMessage('⚠️ Ya existe una receta con ese nombre');
      return;
    }

    setLoading('updatingRecipe', true);
    try {
      const recipeData = {
        name: editingRecipe.name.trim(),
        ingredients: editingRecipe.ingredients,
        laborItems: editingRecipe.laborItems,
        margin: parseFloat(editingRecipe.margin),
        updatedAt: new Date()
      };

      await updateDoc(doc(db, RECIPES_COLLECTION, editingRecipe.id), recipeData);
      
      // Actualización local optimista
      setRecipes(prev => prev.map((recipe, i) => 
        i === index ? { ...editingRecipe, ...recipeData } : recipe
      ));
      resetForm('editing');
      showMessage('Receta actualizada');
    } catch (error) {
      console.error('Error actualizando receta:', error);
      showMessage('❌ Error al actualizar receta');
    }
    setLoading('updatingRecipe', false);
  }, [editingRecipe, recipes, showMessage, setLoading, resetForm]);

  const handleDeleteRecipe = useCallback(async (index) => {
    if (!window.confirm('¿Eliminar esta receta?')) return;
    
    setLoading('deletingRecipe', true);
    try {
      await deleteDoc(doc(db, RECIPES_COLLECTION, recipes[index].id));
      
      // Actualización local optimista
      setRecipes(prev => prev.filter((_, i) => i !== index));
      showMessage('Receta eliminada');
    } catch (error) {
      console.error('Error eliminando receta:', error);
      showMessage('❌ Error al eliminar receta');
    }
    setLoading('deletingRecipe', false);
  }, [recipes, showMessage, setLoading]);

  // ==================== CÁLCULOS DE COSTOS ====================

  /**
   * Calcula el costo total de una receta (memoizado para performance)
   */
  const getRecipeCost = useCallback((recipe) => {
    let total = 0;
    const details = recipe.ingredients.map(ing => {
      const material = rawMaterials.find(mat => mat.name === ing.material);
      const price = material ? material.cost : 0;
      const subtotal = price * ing.quantity;
      total += subtotal;
      return { ...ing, price, subtotal, available: !!material };
    });
    
    const laborDetails = (recipe.laborItems || []).map(laborName => {
      const labor = laborCosts.find(l => l.name === laborName);
      const cost = labor ? labor.cost : 0;
      total += cost;
      return { name: laborName, cost, description: labor?.description || '' };
    });
    
    return { total, details, laborDetails };
  }, [rawMaterials, laborCosts]);

  // ==================== GESTIÓN DE ÓRDENES DE PRODUCCIÓN ====================

  const handleCreateOrder = useCallback(async (e) => {
    e.preventDefault();
    if (!newOrder.operator.trim() || !newOrder.recipeId || !newOrder.quantity || newOrder.quantity <= 0) {
      showMessage('⚠️ Completa todos los campos');
      return;
    }

    const recipe = recipes.find(r => r.id === newOrder.recipeId);
    if (!recipe) {
      showMessage('⚠️ Receta no encontrada');
      return;
    }

    // Calcular insumos requeridos
    const required = recipe.ingredients.map(ing => ({
      material: ing.material,
      quantity: ing.quantity * newOrder.quantity
    }));

    // Verificar stock suficiente
    for (const req of required) {
      const mat = rawMaterials.find(m => m.name === req.material);
      if (!mat || mat.stock < req.quantity) {
        showMessage(`⚠️ Stock insuficiente de ${req.material}`);
        return;
      }
    }

    setLoading('creatingOrder', true);
    try {
      // Descontar insumos en Firebase
      const updatePromises = required.map(req => {
        const mat = rawMaterials.find(m => m.name === req.material);
        return updateDoc(doc(db, RAW_MATERIALS_COLLECTION, mat.id), {
          stock: mat.stock - req.quantity,
          updatedAt: new Date()
        });
      });

      await Promise.all(updatePromises);

      // Actualizar estado local de insumos
      setRawMaterials(prev => prev.map(mat => {
        const req = required.find(r => r.material === mat.name);
        return req ? { ...mat, stock: mat.stock - req.quantity } : mat;
      }));

      // Crear orden
      const orderData = {
        operator: newOrder.operator.trim(),
        recipeId: recipe.id,
        recipeName: recipe.name,
        quantity: newOrder.quantity,
        ingredients: required,
        startedAt: new Date(),
        status: 'active',
        archived: false
      };

      const docRef = await addDoc(collection(db, PRODUCTION_ORDERS_COLLECTION), orderData);
      
      // Actualización local optimista
      setProductionOrders(prev => [...prev, { ...orderData, id: docRef.id }]);
      resetForm('order');
      showMessage('Orden creada y stock descontado');
    } catch (error) {
      console.error('Error creando orden:', error);
      showMessage('❌ Error creando orden');
    }
    setLoading('creatingOrder', false);
  }, [newOrder, recipes, rawMaterials, showMessage, setLoading, resetForm]);

  const handleCompleteOrder = useCallback(async (orderId) => {
    setLoading('updatingOrder', true);
    try {
      await updateDoc(doc(db, PRODUCTION_ORDERS_COLLECTION, orderId), {
        status: 'completed',
        finishedAt: new Date()
      });
      
      // Actualización local optimista
      setProductionOrders(prev => prev.map(o => 
        o.id === orderId ? { ...o, status: 'completed', finishedAt: new Date() } : o
      ));
      showMessage('Orden marcada como concretada');
    } catch (error) {
      console.error('Error actualizando orden:', error);
      showMessage('❌ Error actualizando orden');
    }
    setLoading('updatingOrder', false);
  }, [showMessage, setLoading]);

  const handleArchiveOrder = useCallback(async (orderId, archive) => {
    setLoading('updatingOrder', true);
    try {
      await updateDoc(doc(db, PRODUCTION_ORDERS_COLLECTION, orderId), { 
        archived: archive,
        updatedAt: new Date()
      });
      
      // Actualización local optimista
      setProductionOrders(prev => prev.map(o => 
        o.id === orderId ? { ...o, archived: archive } : o
      ));
      showMessage(archive ? 'Orden archivada' : 'Orden desarchivada');
    } catch (error) {
      console.error('Error actualizando orden:', error);
      showMessage('❌ Error actualizando orden');
    }
    setLoading('updatingOrder', false);
  }, [showMessage, setLoading]);

  const handleDeleteOrder = useCallback(async (order) => {
    if (!window.confirm('¿Eliminar esta orden? Los insumos serán devueltos al stock.')) return;
    
    setLoading('deletingOrder', true);
    try {
      // Devolver insumos en Firebase
      const updatePromises = order.ingredients.map(ing => {
        const mat = rawMaterials.find(m => m.name === ing.material);
        if (mat) {
          return updateDoc(doc(db, RAW_MATERIALS_COLLECTION, mat.id), {
            stock: mat.stock + ing.quantity,
            updatedAt: new Date()
          });
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);

      // Actualizar estado local de insumos
      setRawMaterials(prev => prev.map(mat => {
        const ing = order.ingredients.find(i => i.material === mat.name);
        return ing ? { ...mat, stock: mat.stock + ing.quantity } : mat;
      }));

      // Eliminar orden
      await deleteDoc(doc(db, PRODUCTION_ORDERS_COLLECTION, order.id));
      
      // Actualización local optimista
      setProductionOrders(prev => prev.filter(o => o.id !== order.id));
      showMessage('Orden eliminada y stock restaurado');
    } catch (error) {
      console.error('Error eliminando orden:', error);
      showMessage('❌ Error eliminando orden');
    }
    setLoading('deletingOrder', false);
  }, [rawMaterials, showMessage, setLoading]);

  const handleCancelCompleteOrder = useCallback(async (orderId) => {
    setLoading('updatingOrder', true);
    try {
      await updateDoc(doc(db, PRODUCTION_ORDERS_COLLECTION, orderId), {
        status: 'active',
        finishedAt: null,
        updatedAt: new Date()
      });
      
      // Actualización local optimista
      setProductionOrders(prev => prev.map(o => 
        o.id === orderId ? { ...o, status: 'active', finishedAt: null } : o
      ));
      showMessage('Orden revertida a En curso');
    } catch (error) {
      console.error('Error revirtiendo orden:', error);
      showMessage('❌ Error revirtiendo orden');
    }
    setLoading('updatingOrder', false);
  }, [showMessage, setLoading]);

  // ==================== DATOS MEMOIZADOS PARA PERFORMANCE ====================

  // Calcular valor total de insumos disponibles
  const totalInsumosDisponibles = useMemo(() => {
    return rawMaterials.reduce((sum, mat) => sum + (mat.cost * mat.stock), 0);
  }, [rawMaterials]);

  // Calcular valor total de insumos en producción (solo órdenes en curso, no concretadas)
  const totalInsumosEnProduccion = useMemo(() => {
    return productionOrders
      .filter(order => order.status !== 'completed')
      .reduce((total, order) => {
        return total + order.ingredients.reduce((sum, ing) => {
          const material = rawMaterials.find(m => m.name === ing.material);
          return sum + (material ? material.cost * ing.quantity : 0);
        }, 0);
      }, 0);
  }, [productionOrders, rawMaterials]);

  // Suma total
  const totalInsumosGlobal = totalInsumosDisponibles + totalInsumosEnProduccion;

  const dashboardStats = useMemo(() => {
    const avgRecipeCost = recipes.length > 0 ? 
      recipes.reduce((sum, recipe) => sum + getRecipeCost(recipe).total, 0) / recipes.length : 0;
    const lowStockItems = rawMaterials.filter(mat => mat.stock < 10).length;
    
    return { avgRecipeCost, lowStockItems };
  }, [rawMaterials, recipes, getRecipeCost]);

  const ordersToShow = useMemo(() => 
    productionOrders.filter(o => showArchivedOrders ? o.archived : !o.archived),
    [productionOrders, showArchivedOrders]
  );

  const productionHistoryReport = useMemo(() => {
    const emptyTotals = {
      count: 0,
      units: 0,
      insumos: 0,
      manoObra: 0,
      costo: 0,
      venta: 0,
      ganancia: 0
    };
    let rangeStart = parseLocalDateStart(productionHistoryFrom);
    let rangeEnd = parseLocalDateEnd(productionHistoryTo);
    if (!rangeStart || !rangeEnd) {
      return { rows: [], byOperator: [], totals: emptyTotals, rangeInvalid: true };
    }
    if (rangeEnd < rangeStart) {
      const s = parseLocalDateStart(productionHistoryTo);
      const e = parseLocalDateEnd(productionHistoryFrom);
      if (s && e) {
        rangeStart = s;
        rangeEnd = e;
      }
    }

    const filtered = productionOrders.filter((o) => {
      const s = coerceToDate(o.startedAt);
      return s && s >= rangeStart && s <= rangeEnd;
    });

    const fmtMoney = (n) =>
      n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtDT = (d) =>
      d
        ? d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })
        : '—';

    const rows = filtered.map((order) => {
      const recipe = recipes.find((r) => r.id === order.recipeId);
      const insumosValor = order.ingredients.reduce((sum, ing) => {
        const material = rawMaterials.find((m) => m.name === ing.material);
        return sum + (material ? material.cost * ing.quantity : 0);
      }, 0);
      const recipeCost = recipe ? getRecipeCost(recipe) : { total: 0, laborDetails: [] };
      const unitLabor = (recipeCost.laborDetails || []).reduce((s, l) => s + (Number(l.cost) || 0), 0);
      const qty = Number(order.quantity) || 0;
      const manoObraValor = unitLabor * qty;
      const unitCost = recipeCost.total;
      const costoTotal = unitCost * qty;
      const marginPct = recipe ? Number(recipe.margin) || 0 : 0;
      const ventaTotal = costoTotal * (1 + marginPct / 100);
      const ganancia = ventaTotal - costoTotal;
      const start = coerceToDate(order.startedAt);
      const fin = coerceToDate(order.finishedAt);
      let duracion = '—';
      if (start && fin) {
        const ms = fin.getTime() - start.getTime();
        if (ms < 0) duracion = '—';
        else if (ms < 1000 * 60 * 60 * 24) {
          duracion = `${(ms / (1000 * 60 * 60)).toFixed(1)} h`;
        } else {
          duracion = `${(ms / (1000 * 60 * 60 * 24)).toFixed(1)} días`;
        }
      } else if (order.status !== 'completed') {
        duracion = 'En curso';
      }
      const estado = order.archived ? 'Archivada' : order.status === 'completed' ? 'Finalizada' : 'En curso';
      return {
        id: order.id,
        operator: order.operator || '—',
        product: order.recipeName || '—',
        quantity: qty,
        insumosValor,
        manoObraValor,
        costoTotal,
        ventaTotal,
        ganancia,
        startedAt: start,
        finishedAt: fin,
        duracion,
        estado,
        fmtInsumos: fmtMoney(insumosValor),
        fmtManoObra: fmtMoney(manoObraValor),
        fmtCosto: fmtMoney(costoTotal),
        fmtVenta: fmtMoney(ventaTotal),
        fmtGanancia: fmtMoney(ganancia),
        fmtInicio: fmtDT(start),
        fmtFin: fmtDT(fin)
      };
    });

    const byOpMap = {};
    rows.forEach((r) => {
      const k = r.operator;
      if (!byOpMap[k]) {
        byOpMap[k] = { operator: k, count: 0, units: 0 };
      }
      byOpMap[k].count += 1;
      byOpMap[k].units += r.quantity;
    });
    const byOperator = Object.values(byOpMap).sort((a, b) => b.units - a.units);

    const totals = rows.reduce(
      (acc, r) => ({
        count: acc.count + 1,
        units: acc.units + r.quantity,
        insumos: acc.insumos + r.insumosValor,
        manoObra: acc.manoObra + r.manoObraValor,
        costo: acc.costo + r.costoTotal,
        venta: acc.venta + r.ventaTotal,
        ganancia: acc.ganancia + r.ganancia
      }),
      { ...emptyTotals }
    );

    return {
      rows,
      byOperator,
      totals,
      rangeInvalid: false,
      fmtTotals: {
        insumos: fmtMoney(totals.insumos),
        manoObra: fmtMoney(totals.manoObra),
        costo: fmtMoney(totals.costo),
        venta: fmtMoney(totals.venta),
        ganancia: fmtMoney(totals.ganancia)
      }
    };
  }, [
    productionOrders,
    productionHistoryFrom,
    productionHistoryTo,
    recipes,
    rawMaterials,
    getRecipeCost
  ]);

  // ==================== COMPONENTES DE RENDERIZADO ====================

  const renderDashboard = useCallback(() => {
    // Desestructura correctamente los valores de dashboardStats
    const { avgRecipeCost, lowStockItems } = dashboardStats;

    return (
      <div className="factory-dashboard">
        <h2>🏭 Panel de Control de Producción</h2>
        <p>Controla insumos, fórmulas, costos y órdenes de fabricación en un solo lugar</p>
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon">📦</div>
            <div className="stat-info">
              <div className="stat-number">{rawMaterials.length}</div>
              <div className="stat-label">Insumos Registrados</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📝</div>
            <div className="stat-info">
              <div className="stat-number">{recipes.length}</div>
              <div className="stat-label">Recetas Definidas</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <div className="stat-number nowrap-value">{Number(avgRecipeCost).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              <div className="stat-label">Costo Promedio/Producto</div>
            </div>
          </div>
        </div>
        {/* Resumen de valores totales de insumos junto a los stat-card */}
        <div className="factory-totals-summary">
          <div className="stat-card factory-totals-card factory-totals-card--disponibles">
            <div className="stat-icon" aria-hidden="true">📦</div>
            <div className="stat-info">
              <div className="stat-number nowrap-value">
                {totalInsumosDisponibles.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="stat-label">Insumos Disponibles</div>
            </div>
          </div>
          <div className="stat-card factory-totals-card factory-totals-card--produccion">
            <div className="stat-icon" aria-hidden="true">🏗️</div>
            <div className="stat-info">
              <div className="stat-number nowrap-value">
                {totalInsumosEnProduccion.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="stat-label">Insumos en Producción</div>
            </div>
          </div>
          <div className="stat-card factory-totals-card factory-totals-card--global">
            <div className="stat-icon" aria-hidden="true">💰</div>
            <div className="stat-info">
              <div className="stat-number nowrap-value factory-totals-value--global">
                {totalInsumosGlobal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="stat-label">Total Global</div>
            </div>
          </div>
        </div>
        {lowStockItems > 0 && (
          <div className="alert-section">
            <div className="alert-card">
              <h3>⚠️ Alertas de Stock</h3>
              <p>{lowStockItems} insumo(s) con stock bajo (menos de 10 unidades)</p>
              <button className="factory-action-btn" onClick={() => setActiveTab('materials')}>
                Ver Insumos
              </button>
            </div>
          </div>
        )}
        <div className="quick-actions">
          <h3>⚡ Acciones Rápidas</h3>
          <div className="actions-grid">
            <button 
              className="factory-action-btn"
              onClick={() => setActiveTab('materials')}
            >
              ➕ Registrar Insumo
            </button>
            <button 
              className="factory-action-btn"
              onClick={() => setActiveTab('recipes')}
            >
              📝 Crear Receta
            </button>
            <button 
              className="factory-action-btn"
              onClick={() => setActiveTab('costs')}
            >
              💰 Ver Costos
            </button>
          </div>
        </div>
      </div>
    );
  }, [dashboardStats, rawMaterials.length, recipes.length, totalInsumosDisponibles, totalInsumosEnProduccion, totalInsumosGlobal, setActiveTab]);

  // Definir handleAddCategory antes de renderMaterials
  const handleAddCategory = useCallback(async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) {
      showMessage('⚠️ Ingresa un nombre de categoría');
      return;
    }
    if (materialCategories.some(cat => cat.name.toLowerCase() === newCategory.trim().toLowerCase())) {
      showMessage('⚠️ Ya existe una categoría con ese nombre');
      return;
    }
    try {
      const catData = { name: newCategory.trim(), createdAt: new Date(), updatedAt: new Date() };
      const docRef = await addDoc(collection(db, MATERIAL_CATEGORIES_COLLECTION), catData);
      setMaterialCategories(prev => [...prev, { ...catData, id: docRef.id }]);
      setNewCategory('');
      showMessage('Categoría creada');
    } catch (e) {
      showMessage('❌ Error al crear categoría');
    }
  }, [newCategory, materialCategories, showMessage]);

  // Definir filteredMaterials antes de renderMaterials
  const filteredMaterials = selectedCategoryFilter
    ? rawMaterials.filter(mat => mat.categoryId === selectedCategoryFilter)
    : rawMaterials;

  // Ordenar los materiales: primero los de bajo stock (descendente por valor total), luego el resto (descendente por valor total)
  const sortedMaterials = [...filteredMaterials].sort((a, b) => {
    const lowA = a.stock < 10;
    const lowB = b.stock < 10;
    const totalA = (a.cost || 0) * (a.stock || 0);
    const totalB = (b.cost || 0) * (b.stock || 0);
    if (lowA && !lowB) return -1;
    if (!lowA && lowB) return 1;
    // dentro de cada grupo, ordenar por valor total descendente
    return totalB - totalA;
  });

  const renderMaterials = useCallback(() => {
    // Datos para los gráficos
    let barPieData;
    if (materialsChartMode === 'categorias') {
      // Agrupar por categoría
      const categoryMap = {};
      sortedMaterials.forEach(mat => {
        const catName = materialCategories.find(cat => cat.id === mat.categoryId)?.name || 'Sin categoría';
        const value = (mat.cost || 0) * (mat.stock || 0);
        if (categoryMap[catName]) {
          categoryMap[catName] += value;
        } else {
          categoryMap[catName] = value;
        }
      });
      barPieData = Object.entries(categoryMap)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
    } else {
      // Por insumo
      barPieData = [...sortedMaterials].map(mat => ({
        name: mat.name,
        value: (mat.cost || 0) * (mat.stock || 0)
      })).sort((a, b) => b.value - a.value);
    }

    // Tooltip modo oscuro reutilizable
    const CustomTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
        const data = payload[0];
        // Buscar el material para obtener stock y unidad
        let stock = null;
        let unit = '';
        if (materialsChartMode === 'insumos') {
          const mat = rawMaterials.find(m => m.name === data.name);
          if (mat) {
            stock = mat.stock;
            unit = mat.unit;
          }
        }
        return (
          <div className="custom-tooltip">
            <p className="label">{data.name}</p>
            <p className="value">${data.value.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
            {materialsChartMode === 'insumos' && stock !== null && (
              <p className="value">{stock.toLocaleString('es-AR', { minimumFractionDigits: 2 })} {unit}</p>
            )}
          </div>
        );
      }
      return null;
    };

    return (
      <div className="factory-section">
        <h2>📦 Gestión de Insumos</h2>
        <p>Administra los materiales y componentes utilizados en la producción</p>

        {/* Botón de cambio de modo gráfico */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <button
            className="factory-action-btn"
            style={{ minWidth: 180, fontWeight: 600 }}
            onClick={() => setMaterialsChartMode(mode => mode === 'insumos' ? 'categorias' : 'insumos')}
          >
            Ver por {materialsChartMode === 'insumos' ? 'Categorías' : 'Insumos'}
          </button>
        </div>

        {/* Gráficos de insumos */}
        <div className="factory-charts-row">
          <div className="factory-chart-bar" style={{
            flex: '1 1 350px',
            minWidth: 320,
            maxWidth: 440,
            overflow: 'visible',
            background: chartTheme.cardBg,
            borderRadius: 12,
            padding: 16,
            boxShadow: chartTheme.cardShadow,
            border: `1.5px solid ${chartTheme.cardBorder}`,
            margin: '0 8px',
          }}>
            <h3 className="factory-chart-title">Valor de Insumos (Barra)</h3>
            <ResponsiveContainer width="100%" aspect={1.7}>
              <BarChart data={barPieData} layout="vertical">
                <XAxis type="number" tick={{ fill: chartTheme.tickFill, fontSize: 12 }} axisLine={{ stroke: chartTheme.axisStroke }} tickLine={{ stroke: chartTheme.axisStroke }} />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  tick={{ fill: chartTheme.tickFill, fontSize: 11 }} 
                  width={100} 
                  axisLine={{ stroke: chartTheme.axisStroke }} 
                  tickLine={{ stroke: chartTheme.axisStroke }} 
                />
                <Tooltip content={<CustomTooltip />} followCursor={true} />
                <Bar dataKey="value" fill="#4f8cff" radius={[8, 8, 8, 8]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="factory-chart-pie">
            <h3 className="factory-chart-title">Valor de Insumos (Torta)</h3>
            <div className="chart-pie-container">
              <ResponsiveContainer width="100%" aspect={1.1}>
                <PieChart>
                  <Pie
                    data={barPieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius="80%"
                    fill="#4f8cff"
                    {...(materialsChartMode === 'categorias' ? { label: ({ name }) => name } : {})}
                  >
                    {barPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#4f8cff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'][index % 8]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} followCursor={true} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        
        {/* Filtros y formularios */}
        <div className="materials-filters-forms">
          {/* Filtro de categoría destacado */}
          <div className="category-filter-row">
            <label className="category-filter-label" htmlFor="categoryFilterSelect">Filtrar por categoría:</label>
            <select id="categoryFilterSelect" value={selectedCategoryFilter} onChange={e => setSelectedCategoryFilter(e.target.value)} className="factory-category-select">
              <option value="">Todas</option>
              {materialCategories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          
          {/* En desktop, el formulario y la lista van fuera de section-content */}
          <div className="add-material-form-desktop-wrapper">
            <button
              className="toggle-add-material-btn"
              type="button"
              onClick={() => setShowAddMaterialForm(v => !v)}
              style={{
                width: '100%',
                background: chartTheme.cardBg,
                color: chartTheme.tickFill,
                border: 'none',
                borderRadius: '10px',
                padding: '1rem',
                fontSize: '1.2rem',
                fontWeight: 600,
                marginBottom: '8px',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'block'
              }}
            >
              {showAddMaterialForm ? '✖ Cerrar' : '➕ Agregar Nuevo Insumo'}
              <span style={{ float: 'right', fontWeight: 400, fontSize: '1.1rem' }}>{showAddMaterialForm ? '▲' : '▼'}</span>
            </button>
            {showAddMaterialForm && (
              <div className="add-material-form">
                {/* Formulario para crear categoría, ahora dentro del modal */}
                <div className="add-category-form" style={{ marginBottom: 24 }}>
                  <h4>➕ Crear Categoría de Insumo</h4>
                  <form onSubmit={handleAddCategory} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Nombre de la categoría"
                      value={newCategory}
                      onChange={e => { setNewCategory(e.target.value); }}
                      maxLength="50"
                      className="factory-category-input"
                      style={{ flex: 2 }}
                      autoComplete="off"
                      disabled={false}
                    />
                    <button className="factory-action-btn" type="submit">Crear</button>
                  </form>
                  <div style={{ marginTop: 8, fontSize: '0.95em', color: chartTheme.hintMuted }}>
                    Categorías existentes: {materialCategories.map(cat => cat.name).join(', ') || 'Ninguna'}
                  </div>
                </div>
                {/* Formulario para agregar insumo */}
                <h3>➕ Agregar Nuevo Insumo</h3>
                <form onSubmit={handleAddMaterial} className="material-form-grid">
                  <input
                    type="text"
                    name="name"
                    placeholder="Nombre del insumo *"
                    value={newMaterial.name}
                    onChange={handleMaterialChange}
                    required
                    maxLength="100"
                    disabled={loadingStates.addingMaterial}
                  />
                  <select
                    name="unit"
                    value={newMaterial.unit}
                    onChange={handleMaterialChange}
                    required
                    disabled={loadingStates.addingMaterial}
                  >
                    <option value="">Selecciona unidad *</option>
                    <option value="Unidades">Unidades</option>
                    <option value="Litros">Litros</option>
                    <option value="Kg">Kg</option>
                    <option value="Metros">Metros</option>
                    <option value="M2">M²</option>
                  </select>
                  <input
                    type="number"
                    name="cost"
                    placeholder="Precio actual *"
                    value={newMaterial.cost}
                    onChange={handleMaterialChange}
                    min="0.01"
                    step="0.01"
                    required
                    disabled={loadingStates.addingMaterial}
                  />
                  <input
                    type="number"
                    name="stock"
                    placeholder="Stock inicial (opcional)"
                    value={newMaterial.stock}
                    onChange={handleMaterialChange}
                    min="0"
                    step="0.01"
                    disabled={loadingStates.addingMaterial}
                  />
                  <select
                    name="categoryId"
                    value={newMaterial.categoryId || ''}
                    onChange={e => setNewMaterial(prev => ({ ...prev, categoryId: e.target.value }))}
                    required
                    disabled={loadingStates.addingMaterial}
                  >
                    <option value="">Selecciona categoría *</option>
                    {materialCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <button 
                    type="submit" 
                    className="factory-action-btn" 
                    disabled={loadingStates.addingMaterial}
                  >
                    {loadingStates.addingMaterial ? 'Agregando...' : 'Agregar Insumo'}
                  </button>
                </form>
              </div>
            )}
          </div>
          <div className="materials-list-desktop-wrapper">
            <div className="materials-list">
              {sortedMaterials.length > 0 ? (
                <div className="materials-table-wrapper">
                  <table className="materials-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Unidad</th>
                        <th>Precio</th>
                        <th>Stock</th>
                        <th>Categoría</th>
                        <th>Valor Total</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMaterials.map((material, index) => (
                        editingIndex === rawMaterials.indexOf(material) ? (
                          <tr key={material.id} className="editing-row">
                            <td>
                              <input 
                                type="text" 
                                name="name" 
                                value={editingMaterial.name} 
                                onChange={handleEditingMaterialChange}
                                maxLength="100"
                                disabled={loadingStates.updatingMaterial}
                              />
                            </td>
                            <td>
                              <select
                                name="unit"
                                value={editingMaterial.unit}
                                onChange={handleEditingMaterialChange}
                                disabled={loadingStates.updatingMaterial}
                              >
                                <option value="">Selecciona unidad</option>
                                <option value="Unidades">Unidades</option>
                                <option value="Litros">Litros</option>
                                <option value="Kg">Kg</option>
                                <option value="Metros">Metros</option>
                                <option value="M2">M²</option>
                              </select>
                            </td>
                            <td>
                              <input 
                                type="number" 
                                name="cost" 
                                value={editingMaterial.cost} 
                                onChange={handleEditingMaterialChange} 
                                min="0.01" 
                                step="0.01"
                                disabled={loadingStates.updatingMaterial}
                              />
                            </td>
                            <td>
                              <input 
                                type="number" 
                                name="stock" 
                                value={editingMaterial.stock} 
                                onChange={handleEditingMaterialChange} 
                                min="0" 
                                step="0.01"
                                disabled={loadingStates.updatingMaterial}
                              />
                            </td>
                            <td>
                              <select
                                name="categoryId"
                                value={editingMaterial.categoryId || ''}
                                onChange={e => setEditingMaterial(prev => ({ ...prev, categoryId: e.target.value }))}
                                required
                                disabled={loadingStates.updatingMaterial}
                              >
                                <option value="">Selecciona categoría</option>
                                {materialCategories.map(cat => (
                                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              ${((editingMaterial.cost || 0) * (editingMaterial.stock || 0)).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </td>
                            <td>
                              <button 
                                className="factory-action-btn" 
                                onClick={() => handleSaveEditMaterial(rawMaterials.indexOf(material))}
                                disabled={loadingStates.updatingMaterial}
                              >
                                {loadingStates.updatingMaterial ? 'Guardando...' : 'Guardar'}
                              </button>
                              <button 
                                className="factory-action-btn" 
                                onClick={() => resetForm('editing')}
                                disabled={loadingStates.updatingMaterial}
                              >
                                Cancelar
                              </button>
                            </td>
                          </tr>
                        ) : (
                          <tr key={material.id} className={material.stock < 10 ? 'low-stock' : ''}>
                            <td>
                              {material.name}
                              {material.stock < 10 && <span className="stock-warning"> ⚠️</span>}
                            </td>
                            <td>{material.unit}</td>
                            <td>${material.cost?.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td>{Number(material.stock).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td>{materialCategories.find(cat => cat.id === material.categoryId)?.name || '-'}</td>
                            <td>${(material.cost * material.stock).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td>
                              <button 
                                className="factory-action-btn" 
                                onClick={() => handleEditMaterial(rawMaterials.indexOf(material))}
                                disabled={loadingStates.updatingMaterial || loadingStates.deletingMaterial}
                              >
                                Editar
                              </button>
                              <button 
                                className="factory-action-btn delete-btn" 
                                onClick={() => handleDeleteMaterial(rawMaterials.indexOf(material))}
                                disabled={loadingStates.updatingMaterial || loadingStates.deletingMaterial}
                              >
                                {loadingStates.deletingMaterial ? 'Eliminando...' : 'Eliminar'}
                              </button>
                            </td>
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No hay insumos registrados</p>
                  <p className="empty-subtitle">Agrega el primer insumo para comenzar</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* En mobile/tablet, mantener el layout stackeado dentro de section-content (fallback) */}
        <div className="section-content materials-mobile-stack">
          <div className="add-material-form">
            <h3>➕ Agregar Nuevo Insumo</h3>
            <form onSubmit={handleAddMaterial} className="material-form-grid">
              <input
                type="text"
                name="name"
                placeholder="Nombre del insumo *"
                value={newMaterial.name}
                onChange={handleMaterialChange}
                required
                maxLength="100"
                disabled={loadingStates.addingMaterial}
              />
              <select
                name="unit"
                value={newMaterial.unit}
                onChange={handleMaterialChange}
                required
                disabled={loadingStates.addingMaterial}
              >
                <option value="">Selecciona unidad *</option>
                <option value="Unidades">Unidades</option>
                <option value="Litros">Litros</option>
                <option value="Kg">Kg</option>
                <option value="Metros">Metros</option>
                <option value="M2">M²</option>
              </select>
              <input
                type="number"
                name="cost"
                placeholder="Precio actual *"
                value={newMaterial.cost}
                onChange={handleMaterialChange}
                min="0.01"
                step="0.01"
                required
                disabled={loadingStates.addingMaterial}
              />
              <input
                type="number"
                name="stock"
                placeholder="Stock inicial (opcional)"
                value={newMaterial.stock}
                onChange={handleMaterialChange}
                min="0"
                step="0.01"
                disabled={loadingStates.addingMaterial}
              />
              <select
                name="categoryId"
                value={newMaterial.categoryId || ''}
                onChange={e => setNewMaterial(prev => ({ ...prev, categoryId: e.target.value }))}
                required
                disabled={loadingStates.addingMaterial}
              >
                <option value="">Selecciona categoría *</option>
                {materialCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <button 
                type="submit" 
                className="factory-action-btn" 
                disabled={loadingStates.addingMaterial}
              >
                {loadingStates.addingMaterial ? 'Agregando...' : 'Agregar Insumo'}
              </button>
            </form>
          </div>
          <div className="materials-list">
            {sortedMaterials.length > 0 ? (
              <div className="materials-table-wrapper">
                <table className="materials-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Unidad</th>
                      <th>Precio</th>
                      <th>Stock</th>
                      <th>Categoría</th>
                      <th>Valor Total</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMaterials.map((material, index) => (
                      editingIndex === rawMaterials.indexOf(material) ? (
                        <tr key={material.id} className="editing-row">
                          <td>
                            <input 
                              type="text" 
                              name="name" 
                              value={editingMaterial.name} 
                              onChange={handleEditingMaterialChange}
                              maxLength="100"
                              disabled={loadingStates.updatingMaterial}
                            />
                          </td>
                          <td>
                            <select
                              name="unit"
                              value={editingMaterial.unit}
                              onChange={handleEditingMaterialChange}
                              disabled={loadingStates.updatingMaterial}
                            >
                              <option value="">Selecciona unidad</option>
                              <option value="Unidades">Unidades</option>
                              <option value="Litros">Litros</option>
                              <option value="Kg">Kg</option>
                              <option value="Metros">Metros</option>
                              <option value="M2">M²</option>
                            </select>
                          </td>
                          <td>
                            <input 
                              type="number" 
                              name="cost" 
                              value={editingMaterial.cost} 
                              onChange={handleEditingMaterialChange} 
                              min="0.01" 
                              step="0.01"
                              disabled={loadingStates.updatingMaterial}
                            />
                          </td>
                          <td>
                            <input 
                              type="number" 
                              name="stock" 
                              value={editingMaterial.stock} 
                              onChange={handleEditingMaterialChange} 
                              min="0" 
                              step="0.01"
                              disabled={loadingStates.updatingMaterial}
                            />
                          </td>
                          <td>
                            <select
                              name="categoryId"
                              value={editingMaterial.categoryId || ''}
                              onChange={e => setEditingMaterial(prev => ({ ...prev, categoryId: e.target.value }))}
                              required
                              disabled={loadingStates.updatingMaterial}
                            >
                              <option value="">Selecciona categoría</option>
                              {materialCategories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                              ))}
                            </select>
                          </td>
                          <td>
                            ${((editingMaterial.cost || 0) * (editingMaterial.stock || 0)).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                          </td>
                          <td>
                            <button 
                              className="factory-action-btn" 
                              onClick={() => handleSaveEditMaterial(rawMaterials.indexOf(material))}
                              disabled={loadingStates.updatingMaterial}
                            >
                              {loadingStates.updatingMaterial ? 'Guardando...' : 'Guardar'}
                            </button>
                            <button 
                              className="factory-action-btn" 
                              onClick={() => resetForm('editing')}
                              disabled={loadingStates.updatingMaterial}
                            >
                              Cancelar
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={material.id} className={material.stock < 10 ? 'low-stock' : ''}>
                          <td>
                            {material.name}
                            {material.stock < 10 && <span className="stock-warning"> ⚠️</span>}
                          </td>
                          <td>{material.unit}</td>
                          <td>${material.cost?.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td>{Number(material.stock).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td>{materialCategories.find(cat => cat.id === material.categoryId)?.name || '-'}</td>
                          <td>${(material.cost * material.stock).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td>
                            <button 
                              className="factory-action-btn" 
                              onClick={() => handleEditMaterial(rawMaterials.indexOf(material))}
                              disabled={loadingStates.updatingMaterial || loadingStates.deletingMaterial}
                            >
                              Editar
                            </button>
                            <button 
                              className="factory-action-btn delete-btn" 
                              onClick={() => handleDeleteMaterial(rawMaterials.indexOf(material))}
                              disabled={loadingStates.updatingMaterial || loadingStates.deletingMaterial}
                            >
                              {loadingStates.deletingMaterial ? 'Eliminando...' : 'Eliminar'}
                            </button>
                          </td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <p>No hay insumos registrados</p>
                <p className="empty-subtitle">Agrega el primer insumo para comenzar</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [
    rawMaterials, 
    newMaterial, 
    editingIndex, 
    editingMaterial, 
    loadingStates,
    handleAddMaterial,
    handleMaterialChange,
    handleEditMaterial,
    handleEditingMaterialChange,
    handleSaveEditMaterial,
    handleDeleteMaterial,
    resetForm,
    handleAddCategory,
    materialCategories,
    newCategory,
    selectedCategoryFilter,
    sortedMaterials,
    materialsChartMode,
    showAddMaterialForm,
    setShowAddMaterialForm,
    chartTheme
  ]);

  const renderLabor = useCallback(() => {
    return (
      <div className="factory-section">
        <h2>🧵 Gestión de Mano de Obra</h2>
        <p>Administra los distintos tipos y precios de mano de obra utilizados en la producción</p>
        
        <div className="section-content">
          <div className="add-labor-form">
            <h3>➕ Agregar Mano de Obra</h3>
            <form onSubmit={handleAddLabor} className="material-form-grid">
              <input
                type="text"
                name="name"
                placeholder="Nombre de la mano de obra *"
                value={newLabor.name}
                onChange={handleLaborChange}
                required
                maxLength="100"
                disabled={loadingStates.addingLabor}
              />
              <input
                type="number"
                name="cost"
                placeholder="Precio actual *"
                value={newLabor.cost}
                onChange={handleLaborChange}
                min="0.01"
                step="0.01"
                required
                disabled={loadingStates.addingLabor}
              />
              <input
                type="text"
                name="description"
                placeholder="Descripción (opcional)"
                value={newLabor.description}
                onChange={handleLaborChange}
                maxLength="100"
                disabled={loadingStates.addingLabor}
              />
              <button 
                type="submit" 
                className="factory-action-btn"
                disabled={loadingStates.addingLabor}
              >
                {loadingStates.addingLabor ? 'Agregando...' : 'Agregar Mano de Obra'}
              </button>
            </form>
          </div>
          
          <div className="materials-list">
            <h3>📋 Lista de Mano de Obra ({laborCosts.length})</h3>
            {laborCosts.length > 0 ? (
              <div className="materials-table-wrapper">
                <table className="materials-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Precio</th>
                      <th>Descripción</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {laborCosts.map((labor, index) => (
                      editingLaborIndex === index ? (
                        <tr key={labor.id} className="editing-row">
                          <td>
                            <input 
                              type="text" 
                              name="name" 
                              value={editingLabor.name} 
                              onChange={handleEditingLaborChange}
                              maxLength="100"
                              disabled={loadingStates.updatingLabor}
                            />
                          </td>
                          <td>
                            <input 
                              type="number" 
                              name="cost" 
                              value={editingLabor.cost} 
                              onChange={handleEditingLaborChange} 
                              min="0.01" 
                              step="0.01"
                              disabled={loadingStates.updatingLabor}
                            />
                          </td>
                          <td>
                            <input 
                              type="text" 
                              name="description" 
                              value={editingLabor.description} 
                              onChange={handleEditingLaborChange}
                              maxLength="100"
                              disabled={loadingStates.updatingLabor}
                            />
                          </td>
                          <td>
                            <button 
                              className="factory-action-btn" 
                              onClick={() => handleSaveEditLabor(index)}
                              disabled={loadingStates.updatingLabor}
                            >
                              {loadingStates.updatingLabor ? 'Guardando...' : 'Guardar'}
                            </button>
                            <button 
                              className="factory-action-btn" 
                              onClick={() => resetForm('editing')}
                              disabled={loadingStates.updatingLabor}
                            >
                              Cancelar
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={labor.id}>
                          <td>{labor.name}</td>
                          <td>${labor.cost?.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                          <td>{labor.description}</td>
                          <td>
                            <button 
                              className="factory-action-btn" 
                              onClick={() => handleEditLabor(index)}
                              disabled={loadingStates.updatingLabor || loadingStates.deletingLabor}
                            >
                              Editar
                            </button>
                            <button 
                              className="factory-action-btn delete-btn" 
                              onClick={() => handleDeleteLabor(index)}
                              disabled={loadingStates.updatingLabor || loadingStates.deletingLabor}
                            >
                              {loadingStates.deletingLabor ? 'Eliminando...' : 'Eliminar'}
                            </button>
                          </td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <p>No hay manos de obra registradas</p>
                <p className="empty-subtitle">Agrega la primera mano de obra para comenzar</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [
    laborCosts,
    newLabor,
    editingLaborIndex,
    editingLabor,
    loadingStates,
    handleAddLabor,
    handleLaborChange,
    handleEditLabor,
    handleEditingLaborChange,
    handleSaveEditLabor,
    handleDeleteLabor,
    resetForm
  ]);

  const renderRecipes = useCallback(() => {
    const estimatedNewRecipeCost =
      newRecipe.ingredients.reduce((sum, ing) => {
        const material = rawMaterials.find(mat => mat.name === ing.material);
        return sum + (material ? material.cost * ing.quantity : 0);
      }, 0) +
      newRecipe.laborItems.reduce((sum, laborName) => {
        const labor = laborCosts.find(l => l.name === laborName);
        return sum + (labor ? labor.cost : 0);
      }, 0);

    return (
      <div className="factory-section">
        <h2>📝 Gestión de Recetas</h2>
        <p>Define las fórmulas y procesos para cada producto</p>
        
        <div className="section-content section-content--stacked">
          <div className="add-recipe-form add-recipe-form--create">
            <h3>➕ Crear nueva receta</h3>
            <div className="recipe-instructions recipe-instructions--compact">
              <p><strong>Pasos:</strong> nombre del producto → en la fila de abajo: insumo, cantidad y <em>Añadir</em>; a la derecha, mano de obra opcional y <em>Añadir</em>. Revisá el resumen y guardá.</p>
            </div>
            <form onSubmit={handleAddRecipe} className="create-recipe-form">
              <div className="create-recipe-form__field">
                <label htmlFor="recipe-name" className="create-recipe-form__label">Nombre del producto *</label>
                <input
                  id="recipe-name"
                  type="text"
                  name="name"
                  placeholder="Ej.: nombre del producto terminado"
                  value={newRecipe.name}
                  onChange={handleRecipeChange}
                  maxLength="100"
                  className="material-form-grid input"
                  disabled={loadingStates.addingRecipe}
                />
              </div>

              <div className="create-recipe-form__add-rows">
                <div className="create-recipe-form__panel create-recipe-form__panel--single-line">
                  <div
                    className="create-recipe-form__inline-row"
                    role="group"
                    aria-label="Agregar insumos y mano de obra a la receta"
                  >
                    <div
                      className="create-recipe-form__inline-chunk create-recipe-form__inline-chunk--material"
                      role="group"
                      aria-labelledby="label-recipe-material-group"
                    >
                      <span className="create-recipe-form__group-pill" id="label-recipe-material-group">Insumo</span>
                      <label htmlFor="recipe-new-material" className="visually-hidden">Insumo a agregar</label>
                      <select
                        id="recipe-new-material"
                        name="material"
                        value={ingredientToAdd.material}
                        onChange={e => setIngredientToAdd(prev => ({ ...prev, material: e.target.value }))}
                        className="material-form-grid select create-recipe-form__select-material"
                        disabled={loadingStates.addingRecipe}
                        aria-labelledby="label-recipe-material-group"
                      >
                        <option value="">Seleccioná un insumo…</option>
                        {materialCategories.map(cat => (
                          <optgroup key={cat.id} label={cat.name}>
                            {rawMaterials.filter(mat => mat.categoryId === cat.id).map(mat => (
                              <option key={mat.id} value={mat.name}>
                                {mat.name} (${mat.cost} por {mat.unit})
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <label htmlFor="recipe-new-qty" className="visually-hidden">Cantidad de insumo</label>
                      <input
                        id="recipe-new-qty"
                        type="number"
                        name="quantity"
                        placeholder="Cant."
                        value={ingredientToAdd.quantity}
                        onChange={e => setIngredientToAdd(prev => ({ ...prev, quantity: e.target.value }))}
                        min="0.01"
                        step="0.01"
                        className="material-form-grid input create-recipe-form__qty"
                        disabled={loadingStates.addingRecipe}
                        title="Cantidad"
                        aria-labelledby="label-recipe-material-group"
                      />
                      <button
                        className="factory-action-btn create-recipe-form__add-btn"
                        onClick={handleAddIngredientToRecipe}
                        type="button"
                        disabled={!ingredientToAdd.material || !ingredientToAdd.quantity || loadingStates.addingRecipe}
                        aria-label="Añadir insumo a la lista"
                      >
                        <FaPlus aria-hidden className="create-recipe-form__add-btn-icon" />
                        <span>Añadir</span>
                      </button>
                    </div>

                    <div
                      className="create-recipe-form__inline-chunk create-recipe-form__inline-chunk--labor"
                      role="group"
                      aria-labelledby="label-recipe-labor-group"
                    >
                      <span className="create-recipe-form__group-pill create-recipe-form__group-pill--optional" id="label-recipe-labor-group">
                        Mano de obra <span className="create-recipe-form__optional-badge">Opc.</span>
                      </span>
                      <label htmlFor="recipe-new-labor" className="visually-hidden">Mano de obra a agregar</label>
                      <select
                        id="recipe-new-labor"
                        value={laborToAdd}
                        onChange={e => setLaborToAdd(e.target.value)}
                        className="material-form-grid select create-recipe-form__select-labor"
                        disabled={loadingStates.addingRecipe}
                        aria-labelledby="label-recipe-labor-group"
                      >
                        <option value="">Seleccioná mano de obra…</option>
                        {laborCosts.map((labor) => (
                          <option key={labor.id} value={labor.name}>{labor.name}</option>
                        ))}
                      </select>
                      <button
                        className="factory-action-btn create-recipe-form__add-btn"
                        onClick={handleAddLaborToRecipe}
                        type="button"
                        disabled={!laborToAdd || loadingStates.addingRecipe}
                        aria-label="Añadir mano de obra a la lista"
                      >
                        <FaPlus aria-hidden className="create-recipe-form__add-btn-icon" />
                        <span>Añadir</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {newRecipe.ingredients.length > 0 && (
                <div className="create-recipe-form__table-wrap">
                  <p className="create-recipe-form__table-caption" id="caption-recipe-ingredients">Insumos en esta receta ({newRecipe.ingredients.length})</p>
                  <table className="factory-simple-table factory-simple-table--compact create-recipe-form__table" aria-labelledby="caption-recipe-ingredients">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Cantidad</th>
                      <th>Unidad</th>
                      <th>Unitario</th>
                      <th>Subtotal</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newRecipe.ingredients.map((ing, i) => {
                      const material = rawMaterials.find(mat => mat.name === ing.material);
                      const subtotal = material ? material.cost * ing.quantity : 0;
                      return (
                        <tr key={i}>
                          <td>{ing.material}</td>
                          <td>{ing.quantity}</td>
                          <td>{material?.unit || '-'}</td>
                          <td>${material?.cost || 0}</td>
                          <td>${subtotal.toFixed(2)}</td>
                          <td>
                            <button
                              className="factory-action-btn factory-btn-trash"
                              type="button"
                              onClick={() => handleRemoveIngredientFromRecipe(i)}
                              aria-label="Eliminar ingrediente"
                              disabled={loadingStates.addingRecipe}
                            >
                              <FaTrash size={22} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}

              {newRecipe.laborItems.length > 0 && (
                <div className="create-recipe-form__table-wrap">
                  <p className="create-recipe-form__table-caption" id="caption-recipe-labor">Mano de obra en esta receta ({newRecipe.laborItems.length})</p>
                  <table className="factory-simple-table factory-simple-table--compact create-recipe-form__table" aria-labelledby="caption-recipe-labor">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Costo</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newRecipe.laborItems.map((laborName, i) => {
                      const labor = laborCosts.find(l => l.name === laborName);
                      return (
                        <tr key={i}>
                          <td>{laborName}</td>
                          <td>${labor?.cost || 0}</td>
                          <td>
                            <button
                              className="factory-action-btn factory-btn-trash"
                              type="button"
                              onClick={() => handleRemoveLaborFromRecipe(i)}
                              aria-label="Eliminar mano de obra"
                              disabled={loadingStates.addingRecipe}
                            >
                              <FaTrash size={22} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}

              <div className="create-recipe-form__footer">
                <div className="create-recipe-form__footer-checklist">
                  <div className="create-recipe-requisitos create-recipe-requisitos--inline">
                    <h4 className="create-recipe-form__checklist-title">Estado antes de guardar</h4>
                    <ul>
                      <li className={newRecipe.name.trim() ? 'requirement-met' : 'requirement-missing'}>
                        {newRecipe.name.trim() ? '✓' : '○'} Nombre del producto {newRecipe.name.trim() ? 'listo' : 'pendiente'}
                      </li>
                      <li className={newRecipe.ingredients.length > 0 ? 'requirement-met' : 'requirement-missing'}>
                        {newRecipe.ingredients.length > 0 ? '✓' : '○'} Al menos un insumo {newRecipe.ingredients.length > 0 ? `(${newRecipe.ingredients.length} en lista)` : '(falta agregar)'}
                      </li>
                      <li className={newRecipe.laborItems.length > 0 ? 'requirement-met' : 'requirement-optional'}>
                        {newRecipe.laborItems.length > 0 ? '✓' : '○'} Mano de obra {newRecipe.laborItems.length > 0 ? `(${newRecipe.laborItems.length})` : '(opcional)'}
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="create-recipe-form__footer-actions">
                  {(newRecipe.ingredients.length > 0 || newRecipe.laborItems.length > 0) && (
                    <div className="recipe-preview-total recipe-preview-total--inline">
                      <span className="recipe-preview-total__label">Costo estimado de esta fórmula</span>
                      <strong className="recipe-preview-total__value">
                        ${estimatedNewRecipeCost.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </strong>
                    </div>
                  )}
                  <button
                    type="submit"
                    className="factory-action-btn create-recipe-btn create-recipe-btn--primary"
                    disabled={newRecipe.ingredients.length === 0 || loadingStates.addingRecipe}
                  >
                    {loadingStates.addingRecipe ? 'Guardando…' : 'Guardar receta'}
                  </button>
                </div>
              </div>
            </form>
          </div>
          
          <div className="recipes-list">
            <h3>📋 Recetas Disponibles ({recipes.length})</h3>
            {recipes.length > 0 ? (
              <div className="recipes-table-wrapper">
                <table className="recipes-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Mano de Obra</th>
                      <th>Insumos</th>
                      <th>Costo Total</th>
                      <th>Margen (%)</th>
                      <th>Precio Sugerido</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipes.map((recipe, index) => {
                      const recipeCost = getRecipeCost(recipe);
                      const total = recipeCost.total;
                      const details = recipeCost.details;                      
                      const hasUnavailableIngredients = details.some(d => !d.available);
                      const margin = recipe.margin !== undefined ? parseFloat(recipe.margin) : 30;
                      const suggestedPrice = total * (1 + margin / 100);
                      
                      return editingRecipeIndex === index ? (
                        <tr key={recipe.id} className="editing-row">
                          <td>
                            <input 
                              type="text" 
                              name="name" 
                              value={editingRecipe.name} 
                              onChange={handleEditingRecipeChange}
                              maxLength="100"
                              disabled={loadingStates.updatingRecipe}
                            />
                          </td>
                          <td>
                            <div className="labor-list">
                              {editingRecipe.laborItems.map((laborName, i) => (
                                <div key={i} className="labor-item">
                                  <span>{laborName}</span>
                                  <button 
                                    className="factory-action-btn factory-btn-small" 
                                    type="button" 
                                    onClick={() => handleRemoveLaborFromEditing(i)}
                                    aria-label="Eliminar mano de obra"
                                    disabled={loadingStates.updatingRecipe}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                                  </button>
                                </div>
                              ))}
                              
                              <form onSubmit={handleAddLaborToEditing} className="add-labor-row">
                                <select
                                  name="labor"
                                  value={laborToAddEdit}
                                  onChange={e => setLaborToAddEdit(e.target.value)}
                                  className="flex-2"
                                  disabled={loadingStates.updatingRecipe}
                                >
                                  <option value="">Selecciona mano de obra</option>
                                  {laborCosts.map((labor) => (
                                    <option key={labor.id} value={labor.name}>{labor.name}</option>
                                  ))}
                                </select>
                                <button 
                                  className="factory-action-btn" 
                                  type="submit"
                                  disabled={loadingStates.updatingRecipe}
                                >
                                  Agregar
                                </button>
                              </form>
                            </div>
                          </td>
                          <td>
                            <div className="ingredients-list">
                              {editingRecipe.ingredients.map((ing, i) => (
                                <div key={i} className="ingredient-item">
                                  <select
                                    value={ing.material}
                                    onChange={e => handleEditIngredientChange(i, 'material', e.target.value)}
                                    disabled={loadingStates.updatingRecipe}
                                  >
                                    {materialCategories.map(cat => (
                                      <optgroup key={cat.id} label={cat.name}>
                                        {rawMaterials.filter(mat => mat.categoryId === cat.id).map(mat => (
                                          <option key={mat.id} value={mat.name}>
                                            {mat.name} (${mat.cost} por {mat.unit})
                                          </option>
                                        ))}
                                      </optgroup>
                                    ))}
                                  </select>
                                  <input
                                    type="number"
                                    value={ing.quantity}
                                    min="0.01"
                                    step="0.01"
                                    onChange={e => handleEditIngredientChange(i, 'quantity', e.target.value)}
                                    disabled={loadingStates.updatingRecipe}
                                  />
                                  <button 
                                    className="factory-action-btn factory-btn-small" 
                                    type="button" 
                                    onClick={() => handleRemoveIngredientFromEditing(i)}
                                    aria-label="Eliminar ingrediente"
                                    disabled={loadingStates.updatingRecipe}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                                  </button>
                                </div>
                              ))}
                              
                              <form onSubmit={handleAddIngredientToEditing} className="add-ingredient-row">
                                <select
                                  name="material"
                                  value={ingredientToAddEdit.material}
                                  onChange={e => setIngredientToAddEdit(prev => ({ ...prev, material: e.target.value }))}
                                  disabled={loadingStates.updatingRecipe}
                                >
                                  <option value="">Selecciona insumo</option>
                                  {materialCategories.map(cat => (
                                    <optgroup key={cat.id} label={cat.name}>
                                      {rawMaterials.filter(mat => mat.categoryId === cat.id).map(mat => (
                                        <option key={mat.id} value={mat.name}>
                                          {mat.name} (${mat.cost} por {mat.unit})
                                        </option>
                                      ))}
                                    </optgroup>
                                  ))}
                                </select>
                                <input
                                  type="number"
                                  name="quantity"
                                  placeholder="Cantidad"
                                  value={ingredientToAddEdit.quantity}
                                  onChange={e => setIngredientToAddEdit(prev => ({ ...prev, quantity: e.target.value }))}
                                  min="0.01"
                                  step="0.01"
                                  disabled={loadingStates.updatingRecipe}
                                />
                                <button 
                                  className="factory-action-btn" 
                                  type="submit"
                                  disabled={loadingStates.updatingRecipe}
                                >
                                  Agregar
                                </button>
                              </form>
                            </div>
                          </td>
                          <td>
                            <input
                              type="number"
                              name="margin"
                              placeholder="Margen (%)"
                              value={editingRecipe.margin}
                              onChange={handleEditingRecipeChange}
                              min="0"
                              max="1000"
                              step="0.1"
                              required
                              disabled={loadingStates.updatingRecipe}
                            />
                          </td>
                          <td>
                            <strong className={hasUnavailableIngredients ? 'strong-error' : 'strong-success'}>
                              ${suggestedPrice.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </strong>
                          </td>
                          <td>
                            <button 
                              className="factory-action-btn" 
                              onClick={() => handleSaveEditRecipe(index)}
                              disabled={loadingStates.updatingRecipe}
                            >
                              {loadingStates.updatingRecipe ? 'Guardando...' : 'Guardar'}
                            </button>
                            <button 
                              className="factory-action-btn" 
                              onClick={() => resetForm('editing')}
                              disabled={loadingStates.updatingRecipe}
                            >
                              Cancelar
                            </button>
                          </td>
                        </tr>
                      ) : (
                        <tr key={recipe.id} className={hasUnavailableIngredients ? 'recipe-warning' : ''}>
                          <td>
                            <strong>{recipe.name}</strong>
                            {hasUnavailableIngredients && <span className="warning-badge">⚠️</span>}
                          </td>
                          <td>
                            <ul className="labor-summary">
                              {(recipe.laborItems || []).map((laborName, i) => {
                                const labor = laborCosts.find(l => l.name === laborName);
                                return (
                                  <li key={i} style={{ color: labor ? chartTheme.tickFill : '#e74c3c' }}>
                                    {laborName} - ${labor?.cost || 0}
                                    {!labor && ' (No disponible)'}
                                  </li>
                                );
                              })}
                              {(recipe.laborItems || []).length === 0 && (
                                <li style={{ color: chartTheme.hintMuted, fontStyle: 'italic' }}>Sin mano de obra</li>
                              )}
                            </ul>
                          </td>
                          <td>
                            <ul className="ingredients-summary">
                              {recipe.ingredients.map((ing, i) => {
                                const material = rawMaterials.find(mat => mat.name === ing.material);
                                return (
                                  <li key={i} style={{ color: material ? chartTheme.tickFill : '#e74c3c' }}>
                                    {ing.material} - {ing.quantity} {material?.unit || ''}
                                    {!material && ' (No disponible)'}
                                  </li>
                                );
                              })}
                            </ul>
                          </td>
                          <td>
                            <strong className={hasUnavailableIngredients ? 'strong-error' : 'factory-text-strong'}>
                              ${total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </strong>
                          </td>
                          <td>{margin}%</td>
                          <td>
                            <strong className={hasUnavailableIngredients ? 'strong-error' : 'strong-success'}>
                              ${suggestedPrice.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </strong>
                          </td>
                          <td>
                            <button 
                              className="factory-action-btn" 
                              onClick={() => handleEditRecipe(index)}
                              disabled={loadingStates.updatingRecipe || loadingStates.deletingRecipe}
                            >
                              Editar
                            </button>
                            <button 
                              className="factory-action-btn delete-btn" 
                              onClick={() => handleDeleteRecipe(index)}
                              disabled={loadingStates.updatingRecipe || loadingStates.deletingRecipe}
                            >
                              {loadingStates.deletingRecipe ? 'Eliminando...' : 'Eliminar'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <p>No hay recetas definidas</p>
                <p className="empty-subtitle">
                  {materialCategories.length === 0 
                    ? 'Primero agrega algunas categorías' 
                    : 'Crea la primera receta para comenzar'
                  }
                </p>
                {materialCategories.length === 0 && (
                  <button className="factory-action-btn" onClick={() => setActiveTab('materials')}>
                    Agregar Categorías
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        {/* NUEVO: Pie chart y análisis de producción máxima por receta */}
        {recipes.length > 0 && rawMaterials.length > 0 && (
          <div className="cost-analysis cost-analysis--production-charts">
            <h3>🥧 Producción Máxima con Stock Actual</h3>
            <div className="cost-analysis-graphics">
              {/* Pie Chart */}
              <div className="factory-chart-pie">
                <h4 className="factory-chart-title">Productos que se pueden fabricar</h4>
                <div className="chart-pie-container">
                  <ResponsiveContainer width="100%" aspect={1.1}>
                    <PieChart>
                      <Pie
                        data={recipes.map((recipe, idx) => {
                          let maxUnits = Infinity;
                          recipe.ingredients.forEach(ing => {
                            const mat = rawMaterials.find(m => m.name === ing.material);
                            if (!mat || mat.stock <= 0) {
                              maxUnits = 0;
                            } else {
                              maxUnits = Math.min(maxUnits, Math.floor(mat.stock / ing.quantity));
                            }
                          });
                          return {
                            name: recipe.name,
                            value: maxUnits,
                            color: [
                              '#4f8cff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#e83e8c',
                            ][idx % 8],
                          };
                        })}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius="80%"
                        // Sin label ni labelLine para evitar superposición
                      >
                        {recipes.map((_, idx) => (
                          <Cell key={`cell-pie-prod-${idx}`} fill={[
                            '#4f8cff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#e83e8c',
                          ][idx % 8]} />
                        ))}
                      </Pie>
                      <Tooltip content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0];
                          return (
                            <div className="custom-tooltip">
                              <p className="label">{data.name}</p>
                              <p className="value">{data.value} unidades</p>
                            </div>
                          );
                        }
                        return null;
                      }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Leyenda externa con porcentaje */}
                <div className="chart-pie-legend">
                  {(() => {
                    const pieData = recipes.map((recipe, idx) => {
                      let maxUnits = Infinity;
                      recipe.ingredients.forEach(ing => {
                        const mat = rawMaterials.find(m => m.name === ing.material);
                        if (!mat || mat.stock <= 0) {
                          maxUnits = 0;
                        } else {
                          maxUnits = Math.min(maxUnits, Math.floor(mat.stock / ing.quantity));
                        }
                      });
                      return {
                        name: recipe.name,
                        value: maxUnits,
                        color: [
                          '#4f8cff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#e83e8c',
                        ][idx % 8],
                      };
                    });
                    const total = pieData.reduce((sum, d) => sum + d.value, 0) || 1;
                    return (
                      <div className="chart-pie-legend-container">
                        {pieData.map((d, idx) => (
                          <div key={d.name} className="chart-pie-legend-item">
                            <span className="chart-pie-legend-color" style={{ background: d.color }}></span>
                            <span className="chart-pie-legend-text">{d.name} <span className="chart-pie-legend-percentage">({((d.value / total) * 100).toFixed(0)}%)</span></span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
              {/* Pie Chart: Unidades posibles consumiendo todo el insumo más abundante */}
              <div className="factory-chart-pie">
                <h4 className="factory-chart-title">Productos posibles (consumiendo todo el insumo más abundante)</h4>
                <div className="chart-pie-container">
                  <ResponsiveContainer width="100%" aspect={1.1}>
                    <PieChart>
                      <Pie
                        data={recipes.map((recipe, idx) => {
                          // Calcular máximo absoluto
                          const insumoDetails = recipe.ingredients.map(ing => {
                            const mat = rawMaterials.find(m => m.name === ing.material);
                            const stock = mat ? mat.stock : 0;
                            return ing.quantity > 0 ? stock / ing.quantity : 0;
                          });
                          const maxAbsolute = Math.floor(Math.max(...insumoDetails));
                          return {
                            name: recipe.name,
                            value: maxAbsolute,
                            color: [
                              '#4f8cff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#e83e8c',
                            ][idx % 8],
                          };
                        })}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius="80%"
                      >
                        {recipes.map((_, idx) => (
                          <Cell key={`cell-pie-prod-max-${idx}`} fill={[
                            '#4f8cff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#e83e8c',
                          ][idx % 8]} />
                        ))}
                      </Pie>
                      <Tooltip content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0];
                          return (
                            <div className="custom-tooltip">
                              <p className="label">{data.name}</p>
                              <p className="value">{data.value} unidades</p>
                            </div>
                          );
                        }
                        return null;
                      }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* Leyenda externa con porcentaje */}
                <div className="chart-pie-legend">
                  {(() => {
                    const pieData = recipes.map((recipe, idx) => {
                      const insumoDetails = recipe.ingredients.map(ing => {
                        const mat = rawMaterials.find(m => m.name === ing.material);
                        const stock = mat ? mat.stock : 0;
                        return ing.quantity > 0 ? stock / ing.quantity : 0;
                      });
                      const maxAbsolute = Math.floor(Math.max(...insumoDetails));
                      return {
                        name: recipe.name,
                        value: maxAbsolute,
                        color: [
                          '#4f8cff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#e83e8c',
                        ][idx % 8],
                      };
                    });
                    const total = pieData.reduce((sum, d) => sum + d.value, 0) || 1;
                    return (
                      <div className="chart-pie-legend-container">
                        {pieData.map((d, idx) => (
                          <div key={d.name} className="chart-pie-legend-item">
                            <span className="chart-pie-legend-color" style={{ background: d.color }}></span>
                            <span className="chart-pie-legend-text">{d.name} <span className="chart-pie-legend-percentage">({((d.value / total) * 100).toFixed(0)}%)</span></span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
            {/* Carrusel debajo de los gráficos en desktop */}
            <div className="cost-analysis-carousel">
              <div className="detalle-receta-wrapper detalle-receta-wrapper--carousel">
                <h4 className="detalle-receta-title detalle-receta-title--carousel">Detalle por Receta</h4>
                <Swiper
                  className="detalle-receta-swiper"
                  slidesPerView={1}
                  spaceBetween={12}
                  breakpoints={{
                    768: { slidesPerView: 1, spaceBetween: 18 },
                    1024: { slidesPerView: 1, spaceBetween: 24 }
                  }}
                  navigation
                  pagination={{ clickable: true, dynamicBullets: true }}
                  keyboard={{ enabled: true, onlyInViewport: true }}
                  modules={[Navigation, Pagination, Keyboard]}
                >
                  {recipes.map((recipe, idx) => (
                    <SwiperSlide key={recipe.name}>
                      {(() => {
                    // 1. Calcular para cada insumo cuántos productos se pueden hacer
                    const insumoDetails = recipe.ingredients.map(ing => {
                      const mat = rawMaterials.find(m => m.name === ing.material);
                      const stock = mat ? mat.stock : 0;
                      return {
                        name: ing.material,
                        stock,
                        required: ing.quantity,
                        unit: mat?.unit || '',
                        maxProducts: ing.quantity > 0 ? stock / ing.quantity : 0,
                      };
                    });
                    // 2. Calcular el máximo con stock actual (el mínimo entre insumos)
                    const limiting = insumoDetails.reduce((min, ing) => ing.maxProducts < min.maxProducts ? ing : min, insumoDetails[0]);
                    const maxWithCurrentStock = Math.floor(limiting.maxProducts);
                    // 3. Calcular sobrantes tras fabricar ese máximo
                    const sobrantesActual = insumoDetails.map(ing => {
                      const used = maxWithCurrentStock * ing.required;
                      return {
                        name: ing.name,
                        leftover: ing.stock - used,
                        unit: ing.unit
                      };
                    });
                    // 4. Encontrar el insumo más abundante (el que permite más productos)
                    const mostAbundant = insumoDetails.reduce((max, ing) => ing.maxProducts > max.maxProducts ? ing : max, insumoDetails[0]);
                    const maxAbsoluteUnits = Math.floor(mostAbundant.maxProducts);
                    // 5. Calcular cuánto deberías comprar de los otros insumos para lograr ese máximo
                    const toBuy = insumoDetails.map(ing => {
                      const needed = maxAbsoluteUnits * ing.required;
                      const diff = needed - ing.stock;
                      if (diff > 0.00001) {
                        // Buscar precio del insumo
                        const mat = rawMaterials.find(m => m.name === ing.name);
                        const price = mat ? mat.cost : 0;
                        const subtotal = diff * price;
                        return {
                          name: ing.name,
                          amount: diff,
                          unit: ing.unit,
                          price,
                          subtotal
                        };
                      }
                      return null;
                    }).filter(Boolean);
                    // Calcular el total a comprar
                    const totalToBuy = toBuy.reduce((sum, tb) => sum + tb.subtotal, 0);
                    // 6. Calcular sobrantes tras fabricar ese máximo
                    const sobrantesMax = insumoDetails.map(ing => {
                      const used = maxAbsoluteUnits * ing.required;
                      return {
                        name: ing.name,
                        leftover: ing.stock - used,
                        unit: ing.unit
                      };
                    });
                    return (
                      <div className="recipe-detail-card recipe-detail-card--carousel">
                        <div className="recipe-detail-carousel-grid">
                          <div className="recipe-detail-carousel-col recipe-detail-carousel-col--actual">
                            <div className="recipe-detail-carousel-col-head">Con stock actual</div>
                            <div className="recipe-detail-header-row">
                              <div className="recipe-detail-carousel-head-main">
                                <div className="recipe-detail-title">{recipe.name}</div>
                                <div className="recipe-detail-units">→ <b>{maxWithCurrentStock}</b> unidad(es) posibles</div>
                              </div>
                              <div className="recipe-detail-limiting">
                                Insumo limitante: <b>{limiting.name}</b>
                              </div>
                            </div>
                            <div className="recipe-detail-section recipe-detail-section--carousel">
                              <div className="recipe-detail-section-title">Sobrantes tras fabricar ese máximo</div>
                              <ul className="recipe-detail-list">
                                {sobrantesActual.map(ing => (
                                  <li key={ing.name} className={Math.abs(ing.leftover) > 0.00001 ? 'sobrante' : 'ok'}>
                                    {ing.name}: {ing.leftover.toLocaleString('es-AR', { minimumFractionDigits: 2 })} {ing.unit} {Math.abs(ing.leftover) > 0.00001 ? '(Sobrante)' : '(OK)'}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          <div className="recipe-detail-carousel-col recipe-detail-carousel-col--absolute">
                            <div className="recipe-detail-carousel-col-head">Consumiendo el insumo más abundante</div>
                            <div className="recipe-detail-header-row">
                              <div className="recipe-detail-carousel-head-main">
                                <div className="recipe-detail-units recipe-detail-units--emphasis">→ <b>{maxAbsoluteUnits}</b> unidad(es) posibles</div>
                              </div>
                              <div className="recipe-detail-limiting">
                                Insumo más abundante: <b>{mostAbundant.name}</b>
                              </div>
                            </div>
                            <div className="recipe-detail-content-row recipe-detail-content-row--carousel-split">
                              <div className="recipe-detail-section recipe-detail-section--carousel">
                                <div className="recipe-detail-section-title">Sobrantes tras ese máximo</div>
                                <ul className="recipe-detail-list">
                                  {sobrantesMax.filter(ing => ing.leftover >= 0).map(ing => (
                                    <li key={ing.name} className={Math.abs(ing.leftover) > 0.00001 ? 'sobrante' : 'ok'}>
                                      {ing.name}: {ing.leftover.toLocaleString('es-AR', { minimumFractionDigits: 2 })} {ing.unit} {Math.abs(ing.leftover) > 0.00001 ? '(Sobrante)' : '(OK)'}
                                    </li>
                                  ))}
                                  {sobrantesMax.filter(ing => ing.leftover >= 0).length === 0 && (
                                    <li className="ok">No hay sobrantes</li>
                                  )}
                                </ul>
                              </div>
                              <div className="recipe-detail-section recipe-detail-section--carousel">
                                <div className="recipe-detail-section-title">¿Qué comprar para el resto?</div>
                                <ul className="recipe-detail-list">
                                  {toBuy.length > 0
                                    ? toBuy.map(tb => (
                                      <li key={tb.name} className="comprar">
                                        {tb.name}: {tb.amount.toLocaleString('es-AR', { minimumFractionDigits: 2 })} {tb.unit} × ${tb.price.toLocaleString('es-AR', { minimumFractionDigits: 2 })} = <b>${tb.subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</b>
                                      </li>
                                    ))
                                    : <li className="ok">Nada, stock balanceado</li>}
                                </ul>
                                {toBuy.length > 0 && (
                                  <div className="recipe-detail-total recipe-detail-total--carousel">
                                    Total: <span className="recipe-detail-total-amount">${totalToBuy.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                      })()}
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [
    recipes,
    rawMaterials,
    laborCosts,
    materialCategories,
    newRecipe,
    editingRecipeIndex,
    editingRecipe,
    ingredientToAdd,
    ingredientToAddEdit,
    laborToAdd,
    laborToAddEdit,
    loadingStates,
    getRecipeCost,
    handleAddRecipe,
    handleRecipeChange,
    handleAddIngredientToRecipe,
    handleRemoveIngredientFromRecipe,
    handleAddLaborToRecipe,
    handleRemoveLaborFromRecipe,
    handleEditRecipe,
    handleEditingRecipeChange,
    handleEditIngredientChange,
    handleRemoveIngredientFromEditing,
    handleAddIngredientToEditing,
    handleAddLaborToEditing,
    handleRemoveLaborFromEditing,
    handleSaveEditRecipe,
    handleDeleteRecipe,
    resetForm,
    setActiveTab,
    chartTheme
  ]);

  const renderCosts = useCallback(() => {
    const hasValidRecipes = recipes.some(recipe => {
      const { details } = getRecipeCost(recipe);
      return details.every(d => d.available);
    });

    return (
      <div className="factory-section">
        <h2>💰 Cálculo de Costos</h2>
        <p>Calcula y analiza los costos de producción - Se actualiza automáticamente</p>
        
        <div className="section-content" style={{ gridTemplateColumns: '1fr' }}>
          <div className="cost-calculator">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>🧮 Costos por Producto</h3>
            </div>
            
            {recipes.length > 0 ? (
              <div className="costs-table-wrapper">
                <table className="costs-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Detalle de Insumos</th>
                      <th>Costo Insumos</th>
                      <th>Mano de Obra</th>
                      <th>Costo Total</th>
                      <th>Margen (%)</th>
                      <th>Precio Sugerido</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipes.map((recipe) => {
                      const recipeCost = getRecipeCost(recipe);
                      const total = recipeCost.total;
                      const details = recipeCost.details;
                      const laborDetails = recipeCost.laborDetails;
                      const materialsCost = total - laborDetails.reduce((sum, l) => sum + l.cost, 0);
                      const margin = recipe.margin !== undefined ? parseFloat(recipe.margin) : 30;
                      const suggestedPrice = total * (1 + margin / 100);
                      const hasUnavailableIngredients = details.some(d => !d.available);
                      
                      return (
                        <tr key={recipe.id} className={hasUnavailableIngredients ? 'cost-warning' : ''}>
                          <td>
                            <strong>{recipe.name}</strong>
                            {hasUnavailableIngredients && <div className="warning-text">⚠️ Insumos faltantes</div>}
                          </td>
                          <td>
                            <ul style={{ margin: 0, paddingLeft: '15px' }}>
                              {details.map((ing, i) => (
                                <li 
                                  key={i} 
                                  style={{ 
                                    color: ing.available ? chartTheme.tickFill : '#e74c3c', 
                                    fontSize: '0.9rem', 
                                    marginBottom: '2px' 
                                  }}
                                >
                                  {ing.material} × {ing.quantity} @ ${ing.price?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) || '0.00'} 
                                  = <strong>${ing.subtotal?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) || '0.00'}</strong>
                                  {!ing.available && ' (No disponible)'}
                                </li>
                              ))}
                            </ul>
                          </td>
                          <td>
                            <strong>${materialsCost.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</strong>
                          </td>
                          <td>
                            <ul style={{ margin: 0, paddingLeft: '15px' }}>
                              {laborDetails.map((labor, i) => (
                                <li 
                                  key={i} 
                                  style={{ 
                                    color: labor.cost > 0 ? chartTheme.tickFill : '#e74c3c', 
                                    fontSize: '0.9rem', 
                                    marginBottom: '2px' 
                                  }}
                                >
                                  {labor.name} = <strong>${labor.cost?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) || '0.00'}</strong>
                                  {labor.cost === 0 && ' (No disponible)'}
                                </li>
                              ))}
                              {laborDetails.length === 0 && (
                                <li style={{ color: chartTheme.hintMuted, fontStyle: 'italic', fontSize: '0.9rem' }}>
                                  Sin mano de obra
                                </li>
                              )}
                            </ul>
                            <div style={{ marginTop: '5px', fontWeight: 'bold' }}>
                              Total: ${laborDetails.reduce((sum, l) => sum + l.cost, 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </div>
                          </td>
                          <td>
                            <strong className={hasUnavailableIngredients ? 'strong-error' : 'factory-text-strong'} style={{ fontSize: '1.1rem' }}>
                              ${total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </strong>
                          </td>
                          <td>{margin}%</td>
                          <td>
                            <strong className={hasUnavailableIngredients ? 'strong-error' : 'strong-success'}>
                              ${suggestedPrice.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </strong>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <p>No hay recetas definidas</p>
                <p className="empty-subtitle">Crea recetas para ver el análisis de costos</p>
                <button className="factory-action-btn" onClick={() => setActiveTab('recipes')}>
                  Crear Primera Receta
                </button>
              </div>
            )}
          </div>
          
          {recipes.length > 0 && (
            <div className="cost-analysis">
              <h3>📊 Análisis de Costos</h3>
              
              {!hasValidRecipes && (
                <div className="alert-warning">
                  <p>⚠️ Algunas recetas tienen insumos no disponibles. Los costos pueden no ser precisos.</p>
                </div>
              )}
              
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '15px' 
              }}>
                <div className="analysis-card">
                  <h4>🏆 Producto más costoso</h4>
                  <p>
                    {recipes.reduce((max, recipe) => 
                      getRecipeCost(recipe).total > getRecipeCost(max).total ? recipe : max
                    ).name}
                  </p>
                  <strong>
                    ${getRecipeCost(recipes.reduce((max, recipe) => 
                      getRecipeCost(recipe).total > getRecipeCost(max).total ? recipe : max
                    )).total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </strong>
                </div>
                
                <div className="analysis-card">
                  <h4>💚 Producto más económico</h4>
                  <p>
                    {recipes.reduce((min, recipe) => 
                      getRecipeCost(recipe).total < getRecipeCost(min).total ? recipe : min
                    ).name}
                  </p>
                  <strong>
                    ${getRecipeCost(recipes.reduce((min, recipe) => 
                      getRecipeCost(recipe).total < getRecipeCost(min).total ? recipe : min
                    )).total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </strong>
                </div>
                
                <div className="analysis-card">
                  <h4>📊 Costo promedio</h4>
                  <p>Por producto</p>
                  <strong>
                    ${(recipes.reduce((sum, recipe) => sum + getRecipeCost(recipe).total, 0) / recipes.length)
                      .toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </strong>
                </div>
                
                <div className="analysis-card">
                  <h4>🔧 Mano de obra total</h4>
                  <p>Todos los productos</p>
                  <strong>
                    ${recipes.reduce((sum, recipe) => {
                      const recipeCost = getRecipeCost(recipe);
                      return sum + recipeCost.laborDetails.reduce((laborSum, l) => laborSum + l.cost, 0);
                    }, 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </strong>
                </div>
              </div>
              
              <div className="recommendations">
                <h4>💡 Recomendaciones:</h4>
                <ul>
                  <li>✅ Los costos se actualizan automáticamente cuando cambias precios de insumos</li>
                  <li>🔄 Revisa regularmente los precios de insumos para mantener costos precisos</li>
                  <li>💰 Considera un margen de 25-40% sobre el costo total para determinar precios de venta</li>
                  <li>⚠️ Los insumos marcados en rojo no están disponibles en el inventario</li>
                  <li>📈 Exporta regularmente los reportes de costos para análisis históricos</li>
                  <li>📊 Monitorea las alertas de stock bajo para evitar interrupciones en producción</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }, [recipes, getRecipeCost, setActiveTab, chartTheme]);

  const renderProduction = useCallback(() => {
    return (
      <div className="factory-section">
        <h2>🏗️ Producción en Curso</h2>
        <form onSubmit={handleCreateOrder} className="production-form-grid">
          <input
            type="text"
            name="operator"
            placeholder="Nombre del operario *"
            value={newOrder.operator}
            onChange={e => setNewOrder(prev => ({ ...prev, operator: e.target.value }))}
            required
            disabled={loadingStates.creatingOrder}
            className="material-form-grid input"
          />
          <select
            name="recipeId"
            value={newOrder.recipeId}
            onChange={e => setNewOrder(prev => ({ ...prev, recipeId: e.target.value }))}
            required
            disabled={loadingStates.creatingOrder}
            className="material-form-grid select"
          >
            <option value="">Selecciona producto</option>
            {recipes.map(r => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <input
            type="number"
            name="quantity"
            placeholder="Cantidad"
            value={newOrder.quantity}
            onChange={e => setNewOrder(prev => ({ ...prev, quantity: parseInt(e.target.value) }))}
            min="1"
            required
            disabled={loadingStates.creatingOrder}
            className="material-form-grid input"
          />
          <button 
            type="submit" 
            className="factory-action-btn" 
            disabled={loadingStates.creatingOrder}
          >
            {loadingStates.creatingOrder ? 'Creando...' : 'Crear Orden'}
          </button>
        </form>
        
        {newOrder.recipeId && newOrder.quantity > 0 && (
          <div className="production-summary">
            <h4>Insumos requeridos:</h4>
            <ul>
              {(() => {
                const recipe = recipes.find(r => r.id === newOrder.recipeId);
                if (!recipe) return null;
                return recipe.ingredients.map((ing, i) => {
                  const mat = rawMaterials.find(m => m.name === ing.material);
                  const total = ing.quantity * newOrder.quantity;
                  return (
                    <li key={i} style={{ color: mat && mat.stock >= total ? chartTheme.tickFill : '#e74c3c' }}>
                      {ing.material}: {total} {mat?.unit || ''} (Stock actual: {mat?.stock ?? 'N/A'})
                    </li>
                  );
                });
              })()}
            </ul>
          </div>
        )}
        
        <div style={{ margin: '20px 0' }}>
          <button 
            className="factory-action-btn" 
            onClick={() => setShowArchivedOrders(a => !a)} 
            type="button"
          >
            {showArchivedOrders ? 'Ver Activas' : 'Ver Archivadas'}
          </button>
        </div>
        
        <div className="orders-table-wrapper">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Operario</th>
                <th>Producto</th>
                <th>Cantidad</th>
                <th>Insumos asignados</th>
                <th>Valor total insumos</th>
                <th>Fecha inicio</th>
                <th>Fecha fin</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {ordersToShow.length === 0 ? (
                <tr><td colSpan="9">No hay órdenes</td></tr>
              ) : ordersToShow.map(order => {
                // Calcular el valor total de los insumos asignados
                const totalMaterialsValue = order.ingredients.reduce((total, ing) => {
                  const material = rawMaterials.find(m => m.name === ing.material);
                  return total + (material ? material.cost * ing.quantity : 0);
                }, 0);
                
                return (
                  <tr key={order.id}>
                    <td>{order.operator}</td>
                    <td>{order.recipeName}</td>
                    <td>{order.quantity}</td>
                    <td>
                      <ul style={{ margin: 0, paddingLeft: '15px' }}>
                        {order.ingredients.map((ing, i) => (
                          <li key={i}>{ing.material}: {ing.quantity}</li>
                        ))}
                      </ul>
                    </td>
                    <td>
                      <strong className="factory-text-strong">
                        ${totalMaterialsValue.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                      </strong>
                    </td>
                    <td>{order.startedAt && new Date(order.startedAt.seconds ? order.startedAt.seconds * 1000 : order.startedAt).toLocaleString()}</td>
                    <td>{order.finishedAt ? new Date(order.finishedAt.seconds ? order.finishedAt.seconds * 1000 : order.finishedAt).toLocaleString() : '-'}</td>
                    <td>{order.archived ? 'Archivada' : (order.status === 'completed' ? 'Concretada' : 'En curso')}</td>
                    <td>
                      <div className="production-actions">
                        {order.status !== 'completed' && !order.archived && (
                          <button 
                            className="factory-production-action-btn complete" 
                            onClick={() => handleCompleteOrder(order.id)} 
                            type="button" 
                            title="Finalizar"
                            disabled={loadingStates.updatingOrder}
                          >
                            <FaCheckCircle /> Finalizar
                          </button>
                        )}
                        {order.status === 'completed' && !order.archived && (
                          <button 
                            className="factory-production-action-btn cancel" 
                            onClick={() => handleCancelCompleteOrder(order.id)} 
                            type="button" 
                            title="Cancelar concreción"
                            disabled={loadingStates.updatingOrder}
                          >
                            <FaTimesCircle /> Cancelar
                          </button>
                        )}
                        {!order.archived ? (
                          <button 
                            className="factory-production-action-btn archive" 
                            onClick={() => handleArchiveOrder(order.id, true)} 
                            type="button" 
                            title="Archivar"
                            disabled={loadingStates.updatingOrder}
                          >
                            <FaArchive /> Archivar
                          </button>
                        ) : (
                          <button 
                            className="factory-production-action-btn unarchive" 
                            onClick={() => handleArchiveOrder(order.id, false)} 
                            type="button" 
                            title="Desarchivar"
                            disabled={loadingStates.updatingOrder}
                          >
                            <FaUndo /> Desarchivar
                          </button>
                        )}
                        <button 
                          className="factory-production-action-btn delete" 
                          onClick={() => handleDeleteOrder(order)} 
                          type="button" 
                          title="Eliminar"
                          disabled={loadingStates.deletingOrder}
                        >
                          <FaTrash /> {loadingStates.deletingOrder ? 'Eliminando...' : 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="production-history-panel">
          <h3 className="production-history-title">📋 Historial de producciones</h3>
          <p className="production-history-hint">
            Filtra por <strong>fecha de inicio</strong> de cada orden. Los <strong>totales en dinero</strong> están solo en las tarjetas superiores (sin repetirlos en tablas). <strong>Volumen por operario</strong> resume órdenes y unidades. <strong>Detalle por orden</strong> lista cada producción con importes y fechas. <strong>Valor insumos</strong> = costo de insumos asignados; <strong>Mano de obra</strong> = receta × precios actuales × cantidad; <strong>Costo total</strong> = insumos + mano de obra de la receta; la venta aplica el margen de la receta.
          </p>
          <div
            className="production-history-filters"
            role="group"
            aria-label="Rango de fechas del historial de producción"
          >
            <label className="production-history-filters__field">
              <span className="production-history-filters__label">Desde</span>
              <input
                type="date"
                value={productionHistoryFrom}
                onChange={(e) => setProductionHistoryFrom(e.target.value)}
                className="production-history-filters__date production-history-filters__date--from"
                autoComplete="off"
              />
            </label>
            <label className="production-history-filters__field">
              <span className="production-history-filters__label">Hasta</span>
              <input
                type="date"
                value={productionHistoryTo}
                onChange={(e) => setProductionHistoryTo(e.target.value)}
                className="production-history-filters__date production-history-filters__date--to"
                autoComplete="off"
              />
            </label>
            <button
              type="button"
              className="factory-action-btn production-history-filters__reset"
              onClick={() => {
                const d = new Date();
                const to = d.toISOString().slice(0, 10);
                d.setMonth(d.getMonth() - 1);
                const from = d.toISOString().slice(0, 10);
                setProductionHistoryFrom(from);
                setProductionHistoryTo(to);
              }}
            >
              Último mes
            </button>
          </div>

          {productionHistoryReport.rangeInvalid ? (
            <p className="production-history-empty">Selecciona fechas válidas para el informe.</p>
          ) : (
            <>
              <div className="production-history-summary-bar">
                <div className="production-history-summary-card">
                  <span className="production-history-summary-label">Total valor insumos</span>
                  <strong className="production-history-summary-value accent-cost">
                    ${productionHistoryReport.fmtTotals.insumos}
                  </strong>
                </div>
                <div className="production-history-summary-card">
                  <span className="production-history-summary-label">Total mano de obra</span>
                  <strong className="production-history-summary-value accent-cost">
                    ${productionHistoryReport.fmtTotals.manoObra}
                  </strong>
                </div>
                <div className="production-history-summary-card">
                  <span className="production-history-summary-label">Total costo</span>
                  <strong className="production-history-summary-value accent-cost">
                    ${productionHistoryReport.fmtTotals.costo}
                  </strong>
                </div>
                <div className="production-history-summary-card">
                  <span className="production-history-summary-label">Total venta</span>
                  <strong className="production-history-summary-value accent-sale">
                    ${productionHistoryReport.fmtTotals.venta}
                  </strong>
                </div>
                <div className="production-history-summary-card">
                  <span className="production-history-summary-label">Ganancia total</span>
                  <strong className="production-history-summary-value accent-profit">
                    ${productionHistoryReport.fmtTotals.ganancia}
                  </strong>
                </div>
              </div>

              <h4 className="production-history-subtitle">Volumen por operario</h4>
              <p className="production-history-subtitle-hint">
                Cantidad de órdenes y unidades fabricadas por persona. Los importes están arriba (resumen) y en el detalle por orden.
              </p>
              <div className="orders-table-wrapper production-history-table-wrap">
                <table className="orders-table production-history-table production-history-table--compact">
                  <thead>
                    <tr>
                      <th>Operario</th>
                      <th>Órdenes</th>
                      <th>Unidades</th>
                      <th>% unidades</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productionHistoryReport.byOperator.length === 0 ? (
                      <tr>
                        <td colSpan={4}>No hay órdenes con inicio en este rango.</td>
                      </tr>
                    ) : (
                      productionHistoryReport.byOperator.map((r) => {
                        const pctUnits =
                          productionHistoryReport.totals.units > 0
                            ? ((r.units / productionHistoryReport.totals.units) * 100).toFixed(1)
                            : '0';
                        return (
                          <tr key={r.operator}>
                            <td>{r.operator}</td>
                            <td>{r.count}</td>
                            <td>{r.units}</td>
                            <td>{pctUnits}%</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {productionHistoryReport.byOperator.length > 0 && (
                    <tfoot>
                      <tr className="production-history-tfoot-row">
                        <td>
                          <strong>Totales</strong>
                        </td>
                        <td>{productionHistoryReport.totals.count}</td>
                        <td>{productionHistoryReport.totals.units}</td>
                        <td>100%</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>

              <h4 className="production-history-subtitle">Detalle por orden</h4>
              <div className="orders-table-wrapper production-history-table-wrap">
                <table className="orders-table production-history-table">
                  <thead>
                    <tr>
                      <th>Operario</th>
                      <th>Producto</th>
                      <th>Cantidad</th>
                      <th>Valor insumos</th>
                      <th>Mano de obra</th>
                      <th>Inicio</th>
                      <th>Fin</th>
                      <th>Duración</th>
                      <th>Costo total</th>
                      <th>Venta total</th>
                      <th>Ganancia</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productionHistoryReport.rows.length === 0 ? (
                      <tr>
                        <td colSpan={12}>No hay órdenes con inicio en este rango.</td>
                      </tr>
                    ) : (
                      productionHistoryReport.rows.map((r) => (
                        <tr key={r.id}>
                          <td>{r.operator}</td>
                          <td>{r.product}</td>
                          <td>{r.quantity}</td>
                          <td>${r.fmtInsumos}</td>
                          <td>${r.fmtManoObra}</td>
                          <td>{r.fmtInicio}</td>
                          <td>{r.fmtFin}</td>
                          <td>{r.duracion}</td>
                          <td>${r.fmtCosto}</td>
                          <td>${r.fmtVenta}</td>
                          <td>
                            <strong className="factory-text-strong">${r.fmtGanancia}</strong>
                          </td>
                          <td>{r.estado}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        
        {/* Resumen de valores y gráfico */}
        {ordersToShow.length > 0 && (
          <div className="production-summary-container">
            <div className="production-summary-stats">
              <div className="summary-stat-card">
                <h4>💰 Valor Total en Operarios</h4>
                <div className="summary-stat-value">
                  ${(() => {
                    const totalValue = ordersToShow.reduce((total, order) => {
                      const orderValue = order.ingredients.reduce((sum, ing) => {
                        const material = rawMaterials.find(m => m.name === ing.material);
                        return sum + (material ? material.cost * ing.quantity : 0);
                      }, 0);
                      return total + orderValue;
                    }, 0);
                    return totalValue.toLocaleString('es-AR', { minimumFractionDigits: 2 });
                  })()}
                </div>
                <p>Total de insumos asignados a operarios</p>
              </div>
              
              <div className="summary-stat-card">
                <h4>👥 Operarios Activos</h4>
                <div className="summary-stat-value">
                  {new Set(ordersToShow.map(order => order.operator)).size}
                </div>
                <p>Operarios con órdenes en curso</p>
              </div>
              
              <div className="summary-stat-card">
                <h4>📦 Órdenes Activas</h4>
                <div className="summary-stat-value">
                  {ordersToShow.filter(order => !order.archived && order.status !== 'completed').length}
                </div>
                <p>Órdenes en producción</p>
              </div>
            </div>
            
            <div className="production-chart-container">
              <h4>📊 Distribución de Insumos por Valor</h4>
              <div className="charts-grid">
                {/* Gráfico 1: Distribución por Operario */}
                <div className="chart-section">
                  <h5>👥 Por Operario</h5>
                  <div className="pie-chart">
                    {(() => {
                      // Calcular distribución por operario
                      const operatorDistribution = {};
                      ordersToShow.forEach(order => {
                        const orderValue = order.ingredients.reduce((sum, ing) => {
                          const material = rawMaterials.find(m => m.name === ing.material);
                          return sum + (material ? material.cost * ing.quantity : 0);
                        }, 0);
                        
                        if (operatorDistribution[order.operator]) {
                          operatorDistribution[order.operator] += orderValue;
                        } else {
                          operatorDistribution[order.operator] = orderValue;
                        }
                      });
                      
                      const pieData = Object.entries(operatorDistribution).map(([operator, value]) => ({
                        name: operator,
                        value: value
                      }));
                      
                      const colors = ['#4f8cff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'];
                      const pieTotal = pieData.reduce((sum, item) => sum + item.value, 0);
                      const legendFormatter = (value) => {
                        const item = pieData.find((d) => d.name === value);
                        const pct = pieTotal > 0 && item ? ((item.value / pieTotal) * 100).toFixed(1) : '0';
                        return `${value} (${pct}%)`;
                      };
                      
                      const CustomTooltip = ({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0];
                          const percentage = data.payload.value / pieTotal * 100;
                          return (
                            <div className="custom-tooltip">
                              <p className="label">{data.name}</p>
                              <p className="value">${data.value.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
                              <p className="percentage">({percentage.toFixed(1)}%)</p>
                            </div>
                          );
                        }
                        return null;
                      };
                      
                      return (
                        <ResponsiveContainer width="100%" aspect={1.05}>
                          <PieChart margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              label={false}
                              outerRadius="78%"
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {pieData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} followCursor={true} />
                            <Legend
                              verticalAlign="bottom"
                              layout="horizontal"
                              align="center"
                              formatter={legendFormatter}
                              wrapperClassName="production-pie-legend"
                              wrapperStyle={{
                                color: chartTheme.legendColor,
                                fontSize: 11,
                                paddingTop: 12,
                                width: '100%',
                                lineHeight: 1.35
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </div>
                </div>

                {/* Gráfico 2: Distribución por Tipo de Insumo */}
                <div className="chart-section">
                  <h5>📦 Por Tipo de Insumo</h5>
                  <div className="pie-chart">
                    {(() => {
                      // Calcular distribución por tipo de insumo
                      const materialDistribution = {};
                      ordersToShow.forEach(order => {
                        order.ingredients.forEach(ing => {
                          const material = rawMaterials.find(m => m.name === ing.material);
                          if (material) {
                            const materialValue = material.cost * ing.quantity;
                            if (materialDistribution[ing.material]) {
                              materialDistribution[ing.material] += materialValue;
                            } else {
                              materialDistribution[ing.material] = materialValue;
                            }
                          }
                        });
                      });
                      
                      const pieData = Object.entries(materialDistribution)
                        .sort(([,a], [,b]) => b - a)
                        .map(([material, value]) => ({
                          name: material,
                          value: value
                        }));
                      
                      const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff', '#5f27cd'];
                      const pieTotal = pieData.reduce((sum, item) => sum + item.value, 0);
                      const legendFormatter = (value) => {
                        const item = pieData.find((d) => d.name === value);
                        const pct = pieTotal > 0 && item ? ((item.value / pieTotal) * 100).toFixed(1) : '0';
                        return `${value} (${pct}%)`;
                      };
                      
                      const CustomTooltip = ({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0];
                          const percentage = data.payload.value / pieTotal * 100;
                          return (
                            <div className="custom-tooltip">
                              <p className="label">{data.name}</p>
                              <p className="value">${data.value.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</p>
                              <p className="percentage">({percentage.toFixed(1)}%)</p>
                            </div>
                          );
                        }
                        return null;
                      };
                      
                      return (
                        <ResponsiveContainer width="100%" aspect={1.05}>
                          <PieChart margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              label={false}
                              outerRadius="78%"
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {pieData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} followCursor={true} />
                            <Legend
                              verticalAlign="bottom"
                              layout="horizontal"
                              align="center"
                              formatter={legendFormatter}
                              wrapperClassName="production-pie-legend"
                              wrapperStyle={{
                                color: chartTheme.legendColor,
                                fontSize: 11,
                                paddingTop: 12,
                                width: '100%',
                                lineHeight: 1.35
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [
    newOrder,
    recipes,
    rawMaterials,
    ordersToShow,
    showArchivedOrders,
    loadingStates,
    handleCreateOrder,
    handleCompleteOrder,
    handleCancelCompleteOrder,
    handleArchiveOrder,
    handleDeleteOrder,
    setShowArchivedOrders,
    chartTheme,
    productionHistoryFrom,
    productionHistoryTo,
    productionHistoryReport
  ]);

  // ==================== COMPONENTE PRINCIPAL ====================

  return (
    <div className="factory-container">
      {/* Header */}
      <div className="factory-header">
        <h1>🏭 Gestión de Producción</h1>
        <p>Sistema integral para el control de fabricación</p>
      </div>

      {/* Navegación por Tabs */}
      <div className="factory-navigation">
        <button 
          className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard
        </button>
        <button 
          className={`nav-tab ${activeTab === 'materials' ? 'active' : ''}`}
          onClick={() => setActiveTab('materials')}
        >
          📦 Insumos
        </button>
        <button 
          className={`nav-tab ${activeTab === 'labor' ? 'active' : ''}`}
          onClick={() => setActiveTab('labor')}
        >
          🧵 Mano de Obra
        </button>
        <button 
          className={`nav-tab ${activeTab === 'recipes' ? 'active' : ''}`}
          onClick={() => setActiveTab('recipes')}
        >
          📝 Recetas
        </button>
        <button 
          className={`nav-tab ${activeTab === 'costs' ? 'active' : ''}`}
          onClick={() => setActiveTab('costs')}
        >
          💰 Costos
        </button>
        <button 
          className={`nav-tab ${activeTab === 'production' ? 'active' : ''}`}
          onClick={() => setActiveTab('production')}
        >
          🏗️ Producción
        </button>
      </div>

      {/* Contenido Principal */}
      <div className="factory-content">
        {loadingStates.initial ? (
          <div className="loading-container">
            <Spinner />
            <p>Cargando sistema...</p>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'materials' && renderMaterials()}
            {activeTab === 'labor' && renderLabor()}
            {activeTab === 'recipes' && renderRecipes()}
            {activeTab === 'costs' && renderCosts()}
            {activeTab === 'production' && renderProduction()}
          </>
        )}
      </div>

      {/* Notificaciones */}
      <Notification message={notification.message} show={notification.show} />
    </div>
  );
};

export default Factory;